// js/common/actions/copy-button.js
var CopyButton = class extends HTMLElement {
  constructor() {
    super();
    this.copyToClipboard = this.copyToClipboard.bind(this);
    this.addEventListener("click", this.copyToClipboard);
  }
  async copyToClipboard() {
    if (!navigator.clipboard) {
      return;
    }
    this.querySelector("button")?.setAttribute("aria-busy", "true");
    await navigator.clipboard.writeText(this.getAttribute("data-text") ?? "");
    setTimeout(() => {
      this.querySelector("button")?.setAttribute("aria-busy", "false");
    }, 2e3);
  }
};
if (!window.customElements.get("copy-button")) {
  window.customElements.define("copy-button", CopyButton);
}

// js/common/actions/share-button.js
var ShareButton = class extends HTMLElement {
  constructor() {
    super();
    if (navigator.share) {
      this.querySelector(".share-buttons--native").removeAttribute("hidden");
      this.showSystemShare = this.showSystemShare.bind(this);
      this.addEventListener("click", this.showSystemShare);
    }
  }
  async showSystemShare() {
    try {
      await navigator.share({
        title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
        url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
      });
    } catch (error) {
    }
  }
};
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", ShareButton);
}

// js/common/animation/marquee-text.js
import { inView, animate } from "vendor";

// js/common/utilities/media-query.js
function matchesMediaQuery(breakpointName) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    return false;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).matches;
}
function mediaQueryListener(breakpointName, func) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    throw `Media query ${breakpointName} does not exist`;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).addEventListener("change", func);
}

// js/common/animation/marquee-text.js
var MarqueeText = class extends HTMLElement {
  #resizeObserver;
  #currentAnimation;
  constructor() {
    super();
    this.createElements = this.createElements.bind(this);
    this.initializeShadowDom = this.initializeShadowDom.bind(this);
    this.#resizeObserver = new ResizeObserver(this.createElements);
    inView(this, this.initializeShadowDom, { margin: "400px" });
    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("pointerenter", () => this.#currentAnimation?.pause());
      this.addEventListener("pointerleave", () => this.#currentAnimation?.play());
    }
  }
  static get observedAttributes() {
    return ["speed"];
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "speed" && oldValue !== null && oldValue !== newValue) {
      this.initializeShadowDom();
      this.createElements();
    }
  }
  get isVertical() {
    if (!this.hasAttribute("vertical")) {
      return false;
    }
    return this.getAttribute("vertical") === "" || matchesMediaQuery(this.getAttribute("vertical"));
  }
  get direction() {
    return this.getAttribute("direction") === "end-to-start" ? -1 : 1;
  }
  get scroller() {
    return this.shadowRoot.querySelector('[part="scroller"]');
  }
  initializeShadowDom() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
        <slot part="scroller"></slot>
      `));
      this.#resizeObserver.observe(this);
    }
  }
  createElements() {
    const duplicateCount = this.isVertical ? Math.ceil(this.clientHeight / this.firstElementChild.clientHeight) : Math.ceil(this.clientWidth / this.firstElementChild.clientWidth);
    const fragment = document.createDocumentFragment();
    fragment.appendChild(this.firstElementChild.cloneNode(true));
    for (let i = 1; i <= duplicateCount; ++i) {
      for (let y = 0; y < 2; ++y) {
        const node = this.firstElementChild.cloneNode(true);
        const value = 100 * i * (y % 2 === 0 ? -1 : 1);
        node.setAttribute("aria-hidden", "true");
        node.style.cssText = this.isVertical ? `position: absolute; inset-block-start: ${value}%;` : `position: absolute; inset-inline-start: calc(${value}%);`;
        fragment.appendChild(node);
      }
    }
    this.replaceChildren(fragment);
    let transform = this.isVertical ? ["translateY(0)", `translateY(${this.direction * this.firstElementChild.clientHeight}px)`] : ["translateX(0px)", `translateX(calc(var(--transform-logical-flip) * ${this.direction * this.firstElementChild.clientWidth}px))`];
    let speedMultiplier = this.isVertical ? this.scroller.clientHeight / 300 : this.scroller.clientWidth / 300;
    this.#currentAnimation = animate(this.scroller, { transform }, {
      duration: 1 / parseFloat(this.getAttribute("speed")) * speedMultiplier,
      ease: "linear",
      repeat: Infinity
    });
  }
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

// js/common/behavior/custom-cursor.js
var CustomCursor = class extends HTMLElement {
  #abortController;
  #scheduleSizeRecalculation = true;
  #parentBoundingClientReact;
  #clientWidth;
  #clientHeight;
  constructor() {
    super(...arguments);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    this.parentElement.addEventListener("pointermove", this.onPointerMove, { passive: true, signal: this.#abortController.signal });
    this.parentElement.addEventListener("pointerleave", this.onPointerLeave, { signal: this.#abortController.signal });
    window.addEventListener("scroll", () => this.#scheduleSizeRecalculation = true);
    window.addEventListener("resize", () => this.#scheduleSizeRecalculation = true);

    if (this.hasAttribute("hide-on-swipe")) {
      const carouselId = this.getAttribute("hide-on-swipe");
      setTimeout(() => {
        const carousel = document.getElementById(carouselId);
        if (carousel) {
          carousel.addEventListener("carousel:change", () => {
            this.remove();
          }, { once: true });
        }
      });
    }
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  /**
   * This method is public to allow sub-classes to eventually adjust the logic
   */
  applyTransform({ mouseX, mouseY }) {
    this.animate({ translate: `${mouseX.toFixed(3)}px ${mouseY.toFixed(3)}px` }, { duration: 0, fill: "forwards" });
  }
  onPointerLeave() {
    this.classList.remove("is-visible", "is-half-start", "is-half-end");
  }
  onPointerMove(event) {
    if (this.hasAttribute("hide-over") && event.target.closest(this.getAttribute("hide-over"))) {
      return this.classList.remove("is-visible");
    }
    if (!this.hasAttribute("show-over-links")) {
      const isLink = event.target.matches("button, a[href], button :scope, a[href] :scope");
      if (!this.hasAttribute("for-links") && isLink || this.hasAttribute("for-links") && !isLink) {
        return this.classList.remove("is-visible");
      }
    }
    const { mouseX, mouseY, containerBoundingRect } = this.calculateCoordinates(event);
    this.applyTransform({ mouseX, mouseY, containerBoundingRect });
  }
  /**
   * Retrieving all the dimensions on every pointermove can be expensive, so we precompute them once, and only recalculate them when the window is resized
   */
  precomputeDimensions() {
    if (!this.#scheduleSizeRecalculation) {
      return;
    }
    this.#clientWidth = this.clientWidth;
    this.#clientHeight = this.clientHeight;
    this.#parentBoundingClientReact = this.parentElement.getBoundingClientRect();
    this.#scheduleSizeRecalculation = false;
  }
  calculateCoordinates(event) {
    this.precomputeDimensions();
    const parentBoundingRect = this.#parentBoundingClientReact, parentXCenter = (parentBoundingRect.left + parentBoundingRect.right) / 2, isOnStartHalfPart = event.pageX < parentXCenter;
    this.classList.toggle("is-half-start", isOnStartHalfPart);
    this.classList.toggle("is-half-end", !isOnStartHalfPart);
    this.classList.add("is-visible");
    const mouseY = event.clientY - parentBoundingRect.y - this.#clientHeight / 2, mouseX = event.clientX - parentBoundingRect.x - this.#clientWidth / 2;
    return { mouseX, mouseY, containerBoundingRect: parentBoundingRect };
  }
};
if (!window.customElements.get("custom-cursor")) {
  window.customElements.define("custom-cursor", CustomCursor);
}

// js/common/behavior/gesture-area.js
var GestureArea = class {
  #domElement;
  #thresholdDistance;
  #thresholdTime;
  #signal;
  #firstClientX;
  #tracking = false;
  #start = {};
  constructor(domElement, { thresholdDistance = 40, thresholdTime = 500, signal = null } = {}) {
    this.touchStart = this.touchStart.bind(this);
    this.preventTouch = this.preventTouch.bind(this);
    this.gestureStart = this.gestureStart.bind(this);
    this.gestureMove = this.gestureMove.bind(this);
    this.gestureEnd = this.gestureEnd.bind(this);
    this.#domElement = domElement;
    this.#thresholdDistance = thresholdDistance;
    this.#thresholdTime = thresholdTime;
    this.#signal = signal ?? new AbortController().signal;
    this.#domElement.addEventListener("touchstart", this.touchStart, { passive: true, signal: this.#signal });
    this.#domElement.addEventListener("touchmove", this.preventTouch, { passive: false, signal: this.#signal });
    this.#domElement.addEventListener("pointerdown", this.gestureStart, { signal: this.#signal });
    this.#domElement.addEventListener("pointermove", this.gestureMove, { passive: false, signal: this.#signal });
    this.#domElement.addEventListener("pointerup", this.gestureEnd, { signal: this.#signal });
    this.#domElement.addEventListener("pointerleave", this.gestureEnd, { signal: this.#signal });
    this.#domElement.addEventListener("pointercancel", this.gestureEnd, { signal: this.#signal });
  }
  touchStart(event) {
    this.#firstClientX = event.touches[0].clientX;
  }
  preventTouch(event) {
    if (Math.abs(event.touches[0].clientX - this.#firstClientX) > 10) {
      event.preventDefault();
    }
  }
  gestureStart(event) {
    this.#tracking = true;
    this.#start = {
    time: ( new Date()).getTime(),
    x: event.clientX,
    y: event.clientY
    };
  }
  gestureMove(event) {
    if (this.#tracking) {
      event.preventDefault();
    }
  }
  gestureEnd(event) {
    if (!this.#tracking) {
      return;
    }
    this.#tracking = false;
    const now = ( new Date()).getTime(), deltaTime = now - this.#start.time, deltaX = event.clientX - this.#start.x, deltaY = event.clientY - this.#start.y;
    if (deltaTime > 1500) {
      return;
    }
    let matchedEvent;
    if (deltaX === 0 && deltaY === 0) {
      matchedEvent = "tap";
    } else if (deltaX > this.#thresholdDistance && Math.abs(deltaY) < Math.abs(deltaX)) {
      matchedEvent = "swiperight";
    } else if (-deltaX > this.#thresholdDistance && Math.abs(deltaY) < Math.abs(deltaX)) {
      matchedEvent = "swipeleft";
    } else if (deltaY > this.#thresholdDistance && Math.abs(deltaX) < Math.abs(deltaY)) {
      matchedEvent = "swipedown";
    } else if (-deltaY > this.#thresholdDistance && Math.abs(deltaX) < Math.abs(deltaY)) {
      matchedEvent = "swipeup";
    }
    if (matchedEvent) {
      this.#domElement.dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
    }
  }
};

// js/common/utilities/country-selector.js
var CountrySelector = class extends HTMLElement {
  #onCountryChangedListener = this.onCountryChanged.bind(this);
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement.addEventListener("change", this.#onCountryChangedListener);
    if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country")));
    }
    this.countryElement.dispatchEvent(new Event("change"));
  }
  disconnectedCallback() {
    this.countryElement.removeEventListener("change", this.#onCountryChangedListener);
  }
  onCountryChanged() {
    const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute("data-provinces"));
    this.provinceElement.closest(".form-control").hidden = provinces.length === 0;
    if (provinces.length === 0) {
      return;
    }
    this.provinceElement.innerHTML = "";
    provinces.forEach((data) => {
      const selected = data[1] === this.getAttribute("province") || data[0] === this.getAttribute("province");
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
};
if (!window.customElements.get("country-selector")) {
  window.customElements.define("country-selector", CountrySelector);
}

// js/common/utilities/dimensions.js
function getUntransformedBoundingClientReact(element) {
  let { top, left, width, height } = element.getBoundingClientRect();
  let transformArr = parseTransform(element);
  if (transformArr.length === 0) {
    return { top, left, width, height };
  } else if (transformArr.length === 6) {
    let t = transformArr;
    let det = t[0] * t[3] - t[1] * t[2];
    return {
      width: width / t[0],
      height: height / t[3],
      left: (left * t[3] - top * t[2] + t[2] * t[5] - t[4] * t[3]) / det,
      top: (-left * t[1] + top * t[0] + t[4] * t[1] - t[0] * t[5]) / det
    };
  } else {
    console.warn("Cannot nullify 3D matrix transforms");
  }
}
function parseTransform(element) {
  let transform = window.getComputedStyle(element).transform;
  return transform.split(/\(|,|\)/).slice(1, -1).map((v) => parseFloat(v));
}

// js/common/utilities/cached-fetch.js
var cachedMap =  new Map();
function cachedFetch(url, options) {
  const cacheKey = url;
  if (cachedMap.has(cacheKey)) {
    return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
  }
  return fetch(url, options).then((response) => {
    if (response.status === 200) {
      const contentType = response.headers.get("Content-Type");
      if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
        response.clone().text().then((content) => {
          cachedMap.set(cacheKey, content);
        });
      }
    }
    return response;
  });
}

// js/common/utilities/extract-section-id.js
function extractSectionId(element) {
  if (element.hasAttribute("section-id")) {
    return element.getAttribute("section-id");
  }
  const sectionElement = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
  if (sectionElement) {
    return sectionElement.id.replace("shopify-section-", "");
  }
  const elementWithSectionId = element.closest("[section-id]");
  return elementWithSectionId ? elementWithSectionId.getAttribute("section-id") : null;
}

// js/common/utilities/dom.js
function getSiblings(element) {
  let siblings = [];
  if (!element.parentNode) {
    return siblings;
  }
  let sibling = element.parentNode.firstChild;
  while (sibling) {
    if (sibling.nodeType === 1 && sibling !== element) {
      siblings.push(sibling);
    }
    sibling = sibling.nextSibling;
  }
  return siblings;
}
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template2 of root.querySelectorAll("template")) {
    element = deepQuerySelector(template2.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}

// js/common/utilities/media.js
function videoLoaded(videoOrArray) {
  if (!videoOrArray) {
    return Promise.resolve();
  }
  videoOrArray = videoOrArray instanceof Element ? [videoOrArray] : Array.from(videoOrArray);
  return Promise.all(videoOrArray.map((video) => {
    return new Promise((resolve) => {
      if (video.tagName === "VIDEO" && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA || !video.offsetParent || video.parentNode.hasAttribute("suspended")) {
        resolve();
      } else {
        video.oncanplay = () => resolve();
      }
    });
  }));
}
function imageLoaded(imageOrArray) {
  if (!imageOrArray) {
    return Promise.resolve();
  }
  imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
  return Promise.all(imageOrArray.map((image) => {
    return new Promise((resolve) => {
      if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }));
}
function generateSrcset(imageObjectOrString, widths = []) {
  let imageUrl, maxWidth;
  if (typeof imageObjectOrString === "string") {
    imageUrl = new URL(imageObjectOrString.startsWith("//") ? `https:${imageObjectOrString}` : imageObjectOrString);
    maxWidth = parseInt(imageUrl.searchParams.get("width"));
  } else {
    imageUrl = new URL(imageObjectOrString["src"].startsWith("//") ? `https:${imageObjectOrString["src"]}` : imageObjectOrString["src"]);
    maxWidth = imageObjectOrString["width"];
  }
  return widths.filter((width) => width <= maxWidth).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}
function createMediaImg(media, widths = [], attributes = {}) {
  const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), src = media["preview_image"]["src"], featuredMediaUrl = new URL(src.startsWith("//") ? `https:${src}` : src);
  for (const attributeKey in attributes) {
    image.setAttribute(attributeKey, attributes[attributeKey]);
  }
  image.alt = media["alt"] || "";
  image.src = featuredMediaUrl.href;
  image.srcset = generateSrcset(media["preview_image"], widths);
  return image;
}

// js/common/utilities/player.js
var Player = class extends EventTarget {
  #callback;
  #duration;
  #remainingTime;
  #startTime;
  #timer;
  #state = "paused";
  #mustResumeOnVisibility = true;
  #onVisibilityChangeListener = this.onVisibilityChange.bind(this);
  constructor(durationInSec, stopOnVisibility = true) {
    super();
    this.#callback = () => this.dispatchEvent(new CustomEvent("player:end"));
    if (durationInSec !== void 0) {
      this.setDuration(durationInSec);
    }
    if (stopOnVisibility) {
      document.addEventListener("visibilitychange", this.#onVisibilityChangeListener);
    }
  }
  get paused() {
    return this.#state === "paused";
  }
  setDuration(durationInSec) {
    this.#duration = this.#remainingTime = durationInSec * 1e3;
  }
  pause() {
    if (this.#state !== "started") {
      return;
    }
    clearTimeout(this.#timer);
    this.#state = "paused";
    this.#remainingTime = this.#remainingTime - (( new Date()).getTime() - this.#startTime);
    this.dispatchEvent(new CustomEvent("player:pause", { detail: { duration: this.#duration / 1e3, remainingTime: this.#remainingTime / 1e3 } }));
  }
  resume(restartTimer = false) {
    if (this.#state !== "stopped") {
      if (restartTimer) {
        this.start();
      } else {
        clearTimeout(this.#timer);
        this.#startTime = ( new Date()).getTime();
        this.#state = "started";
        this.#timer = setTimeout(this.#callback, this.#remainingTime);
        this.dispatchEvent(new CustomEvent("player:resume", { detail: { duration: this.#duration / 1e3, remainingTime: this.#remainingTime / 1e3 } }));
      }
    }
  }
  start() {
    clearTimeout(this.#timer);
    this.#startTime = ( new Date()).getTime();
    this.#state = "started";
    this.#remainingTime = this.#duration;
    this.#timer = setTimeout(this.#callback, this.#remainingTime);
    this.dispatchEvent(new CustomEvent("player:start", { detail: { duration: this.#duration / 1e3, remainingTime: this.#remainingTime / 1e3 } }));
  }
  stop() {
    clearTimeout(this.#timer);
    this.#state = "stopped";
    this.dispatchEvent(new CustomEvent("player:stop"));
  }
  onVisibilityChange() {
    if (void 0 === this.#duration) {
      return;
    }
    if (document.visibilityState === "hidden") {
      this.#mustResumeOnVisibility = this.#state === "started";
      this.pause();
      this.dispatchEvent(new CustomEvent("player:visibility-pause"));
    } else if (document.visibilityState === "visible" && this.#mustResumeOnVisibility) {
      this.resume();
      this.dispatchEvent(new CustomEvent("player:visibility-resume"));
    }
  }
};

// js/common/utilities/qr-code.js
var QrCode = class extends HTMLElement {
  connectedCallback() {
    new window.QRCode(this, {
      text: this.getAttribute("identifier"),
      width: this.hasAttribute("width") ? parseInt(this.getAttribute("width")) : 200,
      height: this.hasAttribute("height") ? parseInt(this.getAttribute("height")) : 200
    });
  }
};
if (!window.customElements.get("qr-code")) {
  window.customElements.define("qr-code", QrCode);
}

// js/common/utilities/accessibility.js
function announceText(holderId, message) {
  const holder = document.getElementById(holderId);
  holder.textContent = "";
  setTimeout(() => {
    holder.textContent = message;
  }, 100);
}
function announceStatus(message) {
  announceText("status-announcement", message);
}
function announceError(message) {
  announceText("error-announcement", message);
}

// js/common/behavior/height-observer.js
var HeightObserver = class extends HTMLElement {
  #resizeObserver;
  constructor() {
    super(...arguments);
    this.updateCustomProperties = this.updateCustomProperties.bind(this);
    this.#resizeObserver = new ResizeObserver(throttle(this.updateCustomProperties));
  }
  connectedCallback() {
    this.#resizeObserver.observe(this);
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(2)}px`);
    }
  }
  disconnectedCallback() {
    this.#resizeObserver.unobserve(this);
  }
  updateCustomProperties(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
        document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${height.toFixed(2)}px`);
      }
    });
  }
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}

// js/common/behavior/loading-bar.js
import { animate as animate2 } from "vendor";
var LoadingBar = class extends HTMLElement {
  constructor() {
    super();
    this.onLoadingStart = this.onLoadingStart.bind(this);
    this.onLoadingEnd = this.onLoadingEnd.bind(this);
    document.addEventListener("theme:loading:start", this.onLoadingStart);
    document.addEventListener("theme:loading:end", this.onLoadingEnd);
  }
  onLoadingStart() {
    animate2(this, { opacity: [0, 1], transform: ["scaleX(0)", "scaleX(0.4)"] }, { duration: 0.25 });
  }
  async onLoadingEnd() {
    await animate2(this, { transform: ["scaleX(0)", "scaleX(1)"] }, { duration: 0.25 });
    animate2(this, { opacity: 0 }, { duration: 0.25 });
  }
};
if (!window.customElements.get("loading-bar")) {
  window.customElements.define("loading-bar", LoadingBar);
}

// js/common/behavior/safe-sticky.js
import { inView as inView2 } from "vendor";
var SafeSticky = class extends HTMLElement {
  #resizeObserver;
  #initialTop = 0;
  #lastKnownY = 0;
  // we could initialize it to window.scrollY but this avoids a costly reflow
  #currentTop = 0;
  #position = "relative";
  constructor() {
    super(...arguments);
    this.recalculateStyles = this.recalculateStyles.bind(this);
    this.checkPosition = this.checkPosition.bind(this);
    this.#resizeObserver = new ResizeObserver(this.recalculateStyles);
  }
  connectedCallback() {
    inView2(this, () => {
      window.addEventListener("scroll", this.checkPosition);
      this.#resizeObserver.observe(this);
      return () => {
        window.removeEventListener("scroll", this.checkPosition);
        this.#resizeObserver.unobserve(this);
      };
    }, { margin: "500px" });
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", this.checkPosition);
    this.#resizeObserver.unobserve(this);
  }
  recalculateStyles() {
    this.style.removeProperty("top");
    const computedStyles = getComputedStyle(this);
    this.#initialTop = parseInt(computedStyles.top);
    this.#position = computedStyles.position;
    this.checkPosition();
  }
  checkPosition() {
    if (this.#position !== "sticky") {
      return this.style.removeProperty("top");
    }
    let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + this.#initialTop, minTop = this.clientHeight - window.innerHeight + 20;
    if (window.scrollY < this.#lastKnownY) {
      this.#currentTop = this.#currentTop - (window.scrollY - this.#lastKnownY);
    } else {
      this.#currentTop = this.#currentTop + (this.#lastKnownY - window.scrollY);
    }
    this.#currentTop = Math.min(Math.max(this.#currentTop, -minTop), maxTop, this.#initialTop);
    this.#lastKnownY = window.scrollY;
    this.style.top = `${Math.round(this.#currentTop)}px`;
  }
};
if (!window.customElements.get("safe-sticky")) {
  window.customElements.define("safe-sticky", SafeSticky);
}

// js/common/behavior/sandwich-visibility.js
var SandwichVisibility = class extends HTMLElement {
  #intersectionObserver;
  #isStartMarkerOutOfView = false;
  #isEndMarkerInView = false;
  constructor() {
    super(...arguments);
    this.onObserve = this.onObserve.bind(this);
    this.#intersectionObserver = new IntersectionObserver(this.onObserve);
  }
  connectedCallback() {
    this.#intersectionObserver.observe(this.startMarker);
    this.#intersectionObserver.observe(this.endMarker);
  }
  get startMarker() {
    console.warn("Must return a start marker element.");
  }
  get endMarker() {
    console.warn("Must return an end marker element.");
  }
  onObserve(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.startMarker) {
        this.#isStartMarkerOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      }
      if (entry.target === this.endMarker) {
        this.#isEndMarkerInView = entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight;
      }
    });
    this.classList.toggle("is-visible", this.#isStartMarkerOutOfView && !this.#isEndMarkerInView);
  }
};

// js/common/behavior/scroll-shadow.js
var template = `
  <style>
    :host {
      display: block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      margin: var(--scroll-shadow-margin, 0px);
      background-image:
        var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--background)), rgb(var(--background) / 0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s part="s"></s>
`;
var Updater = class {
  constructor(targetElement) {
    this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
    this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
  }
  start(element) {
    if (this.element) {
      this.stop();
    }
    if (element) {
      element.addEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.observe(element);
      this.element = element;
    }
  }
  stop() {
    if (!this.element) {
      return;
    }
    this.element.removeEventListener("scroll", this.scheduleUpdate);
    this.resizeObserver.unobserve(this.element);
    this.element = null;
  }
  update(targetElement, style) {
    if (!this.element) {
      return;
    }
    const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 30;
    const scroll4 = {
      top: Math.max(this.element.scrollTop, 0),
      bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
      left: Math.max(this.element.scrollLeft, 0),
      right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
    };
    requestAnimationFrame(() => {
      for (const position of ["top", "bottom", "left", "right"]) {
        targetElement.style.setProperty(
          `--${position}`,
          `${scroll4[position] > maxSize ? maxSize : scroll4[position]}px`
        );
      }
    });
  }
};
var ScrollShadow = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).innerHTML = template;
    this.updater = new Updater(this.shadowRoot.lastElementChild);
  }
  connectedCallback() {
    this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
    this.start();
  }
  disconnectedCallback() {
    this.updater.stop();
  }
  start() {
    if (this.firstElementChild) {
      this.updater.start(this.firstElementChild);
    }
  }
};
if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
  window.customElements.define("scroll-shadow", ScrollShadow);
}

// js/common/carousel/scroll-carousel.js
import { inView as inView3 } from "vendor";
var ScrollCarousel = class extends HTMLElement {
  #hasPendingProgrammaticScroll = false;
  #targetIndex = 0;
  #forceChangeEvent = false;
  #dragPosition = {};
  #isDragging = false;
  #dispatchableScrollEvents = { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true };
  #scrollTimeout;

  #resizeObserver;
  #mutationObserver;

  constructor() {
    super();
    this.scrollTo({ left: 0, behavior: "instant" });

    this.setupListeners = this.setupListeners.bind(this);
    this.updateTargetIndex = this.updateTargetIndex.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseClick = this.onMouseClick.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMutate = this.onMutate.bind(this);
    this.adaptHeight = this.adaptHeight.bind(this);
    this.preloadImages = this.preloadImages.bind(this);

    this.setupListeners();
    this.#resizeObserver = new ResizeObserver(this.onResize);
    this.#resizeObserver.observe(this);
    this.#mutationObserver = new MutationObserver(this.onMutate);
    this.#mutationObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ["hidden"] });
  }

  // ---------------------------------------------
  // Lifecycle
  // ---------------------------------------------
  connectedCallback() {
    this.#targetIndex = Math.max(0, this.cells.findIndex((item) => item.classList.contains("is-initial")));
    if (this.#targetIndex > 0) {
      this.select(this.#targetIndex, { instant: true });
    }
    if (this.adaptiveHeight) {
      this.adaptHeight();
    }
    inView3(this, () => this.preloadImages());
  }
  disconnectedCallback() {
    this.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  canChangeSlide() {
    return true;
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return this.#targetIndex;
  }
  get cellAlign() {
    const scrollSnapAlign = getComputedStyle(this.cells[0]).scrollSnapAlign;
    return scrollSnapAlign === "none" ? "center" : scrollSnapAlign;
  }
  get groupCells() {
    if (this.hasAttribute("group-cells")) {
      const number = parseInt(this.getAttribute("group-cells"));
      return isNaN(number) ? Math.floor(this.clientWidth / this.cells[0].clientWidth) : number;
    } else {
      return 1;
    }
  }
  get adaptiveHeight() {
    return this.hasAttribute("adaptive-height");
  }
  get isScrollable() {
    const differenceWidth = this.scrollWidth - this.clientWidth;
    const differenceHeight = this.scrollHeight - this.clientHeight;
    return differenceWidth > 1 || differenceHeight > 1;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  previous({ instant = false } = {}) {
    this.select(Math.max(this.#targetIndex - this.groupCells, 0), { instant });
  }
  next({ instant = false } = {}) {
    this.select(Math.min(this.#targetIndex + this.groupCells, this.cells.length - 1), { instant });
  }
  select(index, { instant = false } = {}) {
    if (!(index in this.cells)) {
      return;
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if ("checkVisibility" in this && this.checkVisibility() || this.offsetWidth > 0 && this.offsetHeight > 0) {
      const targetScrollLeft = this.calculateLeftScroll(this.cells[index]);
      if (this.scrollLeft !== targetScrollLeft) {
        this.updateTargetIndex(index);
        this.#hasPendingProgrammaticScroll = true;
        this.scrollTo({ left: targetScrollLeft, behavior: instant ? "auto" : "smooth" });
      } else {
        this.updateTargetIndex(this.calculateClosestIndexToAlignment());
      }
    } else {
      this.#targetIndex = index;
      this.#forceChangeEvent = true;
    }
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    this.#forceChangeEvent = true;
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  /**
   * Setup all the listeners needed for the carousel to work properly
   */
  setupListeners() {
    if (this.allCells.length > 1) {
      this.addEventListener("carousel:change", this.preloadImages);
      this.addEventListener("scroll", throttle(this.onScroll));
      this.addEventListener("scrollend", this.onScrollEnd);
      if (this.hasAttribute("allow-drag")) {
        const mediaQuery = window.matchMedia("screen and (pointer: fine)");
        mediaQuery.addEventListener("change", (event) => {
          if (event.matches) {
            this.addEventListener("mousedown", this.onMouseDown);
          } else {
            this.removeEventListener("mousedown", this.onMouseDown);
          }
        });
        if (mediaQuery.matches) {
          this.addEventListener("mousedown", this.onMouseDown);
        }
      }
      if (this.adaptiveHeight) {
        this.addEventListener("carousel:settle", this.adaptHeight);
      }
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }));
      }
    }
  }
  updateTargetIndex(newValue) {
    if (newValue === this.#targetIndex && !this.#forceChangeEvent) {
      return;
    }
    this.#targetIndex = newValue;
    this.#forceChangeEvent = false;
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index: newValue, cell: this.cells[newValue] } }));
  }

  // ---------------------------------------------
  // Scroll handling
  // ---------------------------------------------
  onScroll() {
    const scrollEdgeThreshold = 100, normalizedScrollLeft = Math.round(Math.abs(this.scrollLeft - Math.abs(parseInt(getComputedStyle(this).marginInlineStart) || 0)));
    if (normalizedScrollLeft < scrollEdgeThreshold && this.#dispatchableScrollEvents["nearingStart"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "start" } }));
      this.#dispatchableScrollEvents["nearingStart"] = false;
      this.#dispatchableScrollEvents["leavingStart"] = true;
    }
    if (normalizedScrollLeft >= scrollEdgeThreshold && this.#dispatchableScrollEvents["leavingStart"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "start" } }));
      this.#dispatchableScrollEvents["leavingStart"] = false;
      this.#dispatchableScrollEvents["nearingStart"] = true;
    }
    if (this.scrollWidth - this.clientWidth < normalizedScrollLeft + scrollEdgeThreshold && this.#dispatchableScrollEvents["nearingEnd"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "end" } }));
      this.#dispatchableScrollEvents["nearingEnd"] = false;
      this.#dispatchableScrollEvents["leavingEnd"] = true;
    }
    if (this.scrollWidth - this.clientWidth >= normalizedScrollLeft + scrollEdgeThreshold && this.#dispatchableScrollEvents["leavingEnd"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "end" } }));
      this.#dispatchableScrollEvents["leavingEnd"] = false;
      this.#dispatchableScrollEvents["nearingEnd"] = true;
    }
    if (!("onscrollend" in window)) {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent("scrollend", { bubbles: true }));
      }, 75);
    }
    if (this.#hasPendingProgrammaticScroll) {
      return;
    }
    this.updateTargetIndex(this.calculateClosestIndexToAlignment());
  }
  onScrollEnd() {
    this.#hasPendingProgrammaticScroll = false;
    if (!this.#isDragging) {
      this.style.removeProperty("scroll-snap-type");
    }
    this.updateTargetIndex(this.calculateClosestIndexToAlignment());
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
  }

  // ---------------------------------------------
  // Drag handling
  // ---------------------------------------------
  onMouseDown(event) {
    this.#dragPosition = {
      // The current scroll
      left: this.scrollLeft,
      top: this.scrollTop,
      // Get the current mouse position
      x: event.clientX,
      y: event.clientY
    };
    this.#isDragging = true;
    this.style.setProperty("scroll-snap-type", "none");
    this.addEventListener("mousemove", this.onMouseMove);
    this.addEventListener("click", this.onMouseClick, { once: true });
    document.addEventListener("mouseup", this.onMouseUp);
  }
  onMouseMove(event) {
    event.preventDefault();
    const [dx, dy] = [event.clientX - this.#dragPosition.x, event.clientY - this.#dragPosition.y];
    this.scrollTop = this.#dragPosition.top - dy;
    this.scrollLeft = this.#dragPosition.left - dx;
  }
  onMouseClick(event) {
    if (event.clientX - this.#dragPosition.x !== 0) {
      event.preventDefault();
    }
  }
  onMouseUp(event) {
    this.#isDragging = false;
    if (event.clientX - this.#dragPosition.x === 0) {
      this.style.removeProperty("scroll-snap-type");
    } else if (!this.#hasPendingProgrammaticScroll) {
      this.scrollTo({ left: this.calculateLeftScroll(this.selectedCell), behavior: "smooth" });
    }
    this.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  }

  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  /**
   * Calculate the amount to scroll to align the cell with the "cell-align" rule
   */
  calculateLeftScroll(cell) {
    let scrollLeft;
    switch (this.cellAlign) {
      case "start":
        scrollLeft = document.dir === "ltr" ? cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) - (parseInt(getComputedStyle(this).marginInlineStart) || 0) - (parseInt(getComputedStyle(this).paddingInlineStart) || 0) : cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) + (parseInt(getComputedStyle(this).marginInlineStart) || 0) + (parseInt(getComputedStyle(this).paddingInlineStart) || 0);
        break;
      case "center":
        scrollLeft = Math.round(cell.offsetLeft - this.clientWidth / 2 + cell.clientWidth / 2);
        break;
      case "end":
        scrollLeft = document.dir === "ltr" ? cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0) + (parseInt(getComputedStyle(this).marginInlineEnd) || 0) + (parseInt(getComputedStyle(this).paddingInlineEnd) || 0) : cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0) - (parseInt(getComputedStyle(this).marginInlineEnd) || 0) - (parseInt(getComputedStyle(this).paddingInlineEnd) || 0);
        break;
    }
    return document.dir === "ltr" ? Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth) : Math.min(Math.max(scrollLeft, this.clientWidth - this.scrollWidth), 0);
  }
  calculateClosestIndexToAlignment() {
    let cellAlign = this.cellAlign, offsetAccumulators, targetPoint;
    if (cellAlign === "center") {
      offsetAccumulators = this.cells.map((cell) => Math.round(cell.offsetLeft + cell.clientWidth / 2));
      targetPoint = Math.round(this.scrollLeft + this.clientWidth / 2);
    } else if (cellAlign === "start" && document.dir === "ltr" || cellAlign === "end" && document.dir === "rtl") {
      offsetAccumulators = this.cells.map((cell) => cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) - (parseInt(getComputedStyle(this).marginInlineStart) || 0) - (parseInt(getComputedStyle(this).paddingInlineStart) || 0));
      targetPoint = this.scrollLeft;
    } else {
      offsetAccumulators = this.cells.map((cell) => cell.offsetLeft + cell.clientWidth);
      targetPoint = this.scrollLeft + this.clientWidth;
    }
    return offsetAccumulators.indexOf(offsetAccumulators.reduce((prev, curr) => Math.abs(curr - targetPoint) < Math.abs(prev - targetPoint) ? curr : prev));
  }
  onResize() {
    if (this.selectedIndex !== this.calculateClosestIndexToAlignment()) {
      this.select(this.selectedIndex, { instant: true });
    }
    if (this.adaptiveHeight) {
      this.adaptHeight();
    }
    this.classList.toggle("is-scrollable", this.scrollWidth > this.clientWidth);
  }
  onMutate() {
    this.#forceChangeEvent = true;
  }
  adaptHeight() {
    if (this.clientHeight === this.selectedCell.clientHeight) {
      return;
    }
    this.style.maxHeight = null;
    if (this.isScrollable) {
      this.style.maxHeight = `${this.selectedCell.clientHeight}px`;
    }
  }
  preloadImages() {
    requestAnimationFrame(() => {
      const previousSlide = this.cells[Math.max(this.selectedIndex - 1, 0)], nextSlide = this.cells[Math.min(this.selectedIndex + 1, this.cells.length - 1)];
      [previousSlide, this.selectedCell, nextSlide].filter((item) => item !== null).forEach((item) => {
        Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
      });
    });
  }
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// js/common/carousel/carousel-navigation.js
var CarouselNavigation = class extends HTMLElement {
  #abortController;
  #allItems = [];
  constructor() {
    super(...arguments);
    this.onCarouselFilter = this.onCarouselFilter.bind(this);
  }
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel navigation component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.#allItems = Array.from(this.querySelectorAll("button"));
    this.#allItems.forEach((button) => button.addEventListener("click", () => this.onButtonClicked(this.items.indexOf(button)), { signal: this.#abortController.signal }));
    this.carousel.addEventListener("carousel:change", (event) => this.onNavigationChange(event.detail.index), { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:filter", this.onCarouselFilter, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  get items() {
    return this.#allItems.filter((item) => !item.hasAttribute("hidden"));
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  get selectedIndex() {
    return this.#allItems.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  onButtonClicked(newIndex) {
    if (this.carousel.canChangeSlide()) {
      this.carousel.select(newIndex);
      this.onNavigationChange(newIndex);
    }
  }
  onNavigationChange(newIndex) {
    this.items.forEach((button, index) => button.setAttribute("aria-current", newIndex === index ? "true" : "false"));
    if (this.hasAttribute("align-selected") && (this.scrollWidth !== this.clientWidth || this.scrollHeight !== this.clientHeight)) {
      this.scrollTo({
        left: this.items[newIndex].offsetLeft - this.clientWidth / 2 + this.items[newIndex].clientWidth / 2,
        top: this.items[newIndex].offsetTop - this.clientHeight / 2 + this.items[newIndex].clientHeight / 2,
        behavior: matchesMediaQuery("motion-safe") ? "smooth" : "auto"
      });
    }
  }
  onCarouselFilter(event) {
    this.#allItems.forEach((item, index) => {
      item.toggleAttribute("hidden", (event.detail.filteredIndexes || []).includes(index));
    });
  }
};

var CarouselDirectionButton = class extends HTMLElement {
  #abortController;
  #mutationObserver;
  constructor() {
    super(...arguments);
    this.onClick = this.onClick.bind(this);
    this.onCheckScrollability = this.onCheckScrollability.bind(this);
    this.#mutationObserver = new MutationObserver(this.onCheckScrollability);
  }
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel prev button component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    const listenerOptions = { signal: this.#abortController.signal };
    this.addEventListener("click", this.onClick, listenerOptions);
    this.carousel.addEventListener("scroll:edge-nearing", (event) => this.firstElementChild.disabled = event.detail.position === this.direction, listenerOptions);
    this.carousel.addEventListener("scroll:edge-leaving", (event) => this.firstElementChild.disabled = event.detail.position === this.direction ? false : this.firstElementChild.disabled, listenerOptions);
    this.#mutationObserver.observe(this.carousel, { attributes: true, attributeFilter: ["class"], childList: false, subtree: false });
    this.onCheckScrollability();
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  get direction() {
    throw "Carousel direction button cannot be used directly. Use <carousel-prev-button> or <carousel-next-button> instead.";
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  onClick() {
    this.direction === "start" ? this.carousel.previous() : this.carousel.next();
  }
  onCheckScrollability() {
    if (this.carousel instanceof ScrollCarousel) {
      this.toggleAttribute("hidden", !this.carousel.isScrollable);
    }
  }
};

var CarouselPrevButton = class extends CarouselDirectionButton {
  get direction() {
    return "start";
  }
};
var CarouselNextButton = class extends CarouselDirectionButton {
  get direction() {
    return "end";
  }
};
var CarouselPlayerButton = class extends HTMLElement {
  constructor() {
    super();
    this.togglePlayer = this.togglePlayer.bind(this);
    this.onPlayerStart = this.onPlayerStart.bind(this);
    this.addEventListener("click", this.togglePlayer);
    this.carousel.addEventListener("carousel:select", this.onPlayerStart);
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  togglePlayer() {
    const button = this.querySelector("button");
    button.firstElementChild.classList.toggle("hidden");
    button.lastElementChild.classList.toggle("hidden");
    if (this.carousel.player.paused) {
      this.carousel.player.resume();
    } else {
      this.carousel.player.pause();
    }
  }
  onPlayerStart() {
    const button = this.querySelector("button");
    button.firstElementChild.classList.remove("hidden");
    button.lastElementChild.classList.add("hidden");
  }
};

var CarouselPageIndicator = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.updateIndicator = this.updateIndicator.bind(this);
  }
  connectedCallback() {
    this.carousel.addEventListener("carousel:change", this.updateIndicator);
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  get currentSlideIndex() {
    return this.querySelector("[current-slide-index]");
  }
  get pageSize() {
    return this.querySelector("[page-size]");
  }
  updateIndicator(event) {
    let pageSize = 0;
    Array.from(this.carousel.children).forEach((item) => {
      if (!item.hidden) {
        pageSize += 1;
      }
    });
    this.pageSize.innerText = pageSize.toString();
    this.currentSlideIndex.innerText = (event.detail.index + 1).toString();
  }
};
if (!window.customElements.get("carousel-prev-button")) {
  window.customElements.define("carousel-prev-button", CarouselPrevButton);
}
if (!window.customElements.get("carousel-next-button")) {
  window.customElements.define("carousel-next-button", CarouselNextButton);
}
if (!window.customElements.get("carousel-navigation")) {
  window.customElements.define("carousel-navigation", CarouselNavigation);
}
if (!window.customElements.get("carousel-player-button")) {
  window.customElements.define("carousel-player-button", CarouselPlayerButton);
}
if (!window.customElements.get("carousel-page-indicator")) {
  window.customElements.define("carousel-page-indicator", CarouselPageIndicator);
}

// js/common/carousel/effect-carousel.js
import { animate as animate3, animateSequence, inView as inView4 } from "vendor";
var EffectCarousel = class extends HTMLElement {
  #listenersAbortController;
  #gestureArea;
  #player;
  #targetIndex = 0;
  #preventInitialTransition = false;
  #hasPendingTransition = false;
  constructor() {
    super();

    this.setupListeners = this.setupListeners.bind(this);
    this.onKeyboardNavigation = this.onKeyboardNavigation.bind(this);
    this.preloadImages = this.preloadImages.bind(this);

    this.setupListeners();
    inView4(this, () => this.onBecameVisible());
    this.addEventListener("carousel:settle", (event) => {
      this.allCells.forEach((cell) => cell.classList.toggle("is-selected", cell === event.detail.cell));
    });
  }
  connectedCallback() {
    this.#targetIndex = Math.max(0, this.cells.findIndex((item) => item.classList.contains("is-selected")));
    inView4(this, () => this.preloadImages());
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  get allowSwipe() {
    return this.hasAttribute("allow-swipe");
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get blockChangeWhenTransitioning() {
    return false;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return this.#targetIndex;
  }
  get player() {
    return this.#player;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  canChangeSlide() {
    return !(this.blockChangeWhenTransitioning && this.#hasPendingTransition);
  }
  previous({ instant = false } = {}) {
    return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, { instant, direction: "previous" });
  }
  next({ instant = false } = {}) {
    return this.select((this.selectedIndex + 1 + this.cells.length) % this.cells.length, { instant, direction: "next" });
  }
  async select(index, { instant = false, direction = null } = {}) {
    if (!(index in this.cells) || this.blockChangeWhenTransitioning && this.#hasPendingTransition) {
      return Promise.resolve();
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if (index === this.selectedIndex) {
      return Promise.resolve();
    }
    this.#player?.pause();
    const [fromSlide, toSlide] = [this.selectedCell, this.cells[index]];
    direction ??= index > this.selectedIndex ? "next" : "previous";
    this.#targetIndex = index;
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: this.cells[index] } }));
    const animationControls = this.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
    this.#hasPendingTransition = true;
    if ("leaveControls" in animationControls && "enterControls" in animationControls) {
      const leaveAnimationControls = animationControls.leaveControls();
      if (instant) {
        leaveAnimationControls.complete();
      }
      await leaveAnimationControls;
      this.#player?.setDuration(await this.getPlayerDurationForSlide(toSlide));
      this.#player?.resume(true);
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");
      const enterAnimationControls = animationControls.enterControls();
      if (instant) {
        enterAnimationControls.complete();
      }
      await enterAnimationControls;
    } else {
      if (instant) {
        animationControls.complete();
      }
      this.#player?.setDuration(await this.getPlayerDurationForSlide(toSlide));
      this.#player?.resume(true);
      toSlide.classList.add("is-selected");
      await animationControls;
      fromSlide.classList.remove("is-selected");
    }
    this.#hasPendingTransition = false;
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: this.cells[index] } }));
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  async onBecameVisible() {
    const animationControls = this.createOnBecameVisibleAnimationControls(this.selectedCell);
    [this.selectedCell, ...this.selectedCell.querySelectorAll("[reveal-on-scroll]")].forEach((element) => {
      element.removeAttribute("reveal-on-scroll");
    });
    if (this.#preventInitialTransition && typeof animationControls.complete === "function") {
      animationControls.complete();
    }
    return animationControls.then(async () => {
      this.#player?.setDuration(await this.getPlayerDurationForSlide(this.selectedCell));
      this.#player?.resume(true);
      this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
    });
  }
  /**
   * The animation controls when the carousel enter into the view for the first time (by default, none)
   */
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate3(toSlide, {}, { duration: 0 });
  }
  /**
   * Define the transition when the slide changes
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    return animateSequence([
      [fromSlide, { opacity: [1, 0] }, { duration: 0.3 }],
      [toSlide, { opacity: [0, 1] }, { duration: 0.3, at: "<" }]
    ]);
  }
  /**
   * This method is called whenever the slide changes. By default, it uses the duration set on the autoplay attribute,
   * but this can be overridden for each slide (the argument gives the new current slide). This can be useful for
   * slideshow for instance, to have a different duration for video slide.
   *
   * Please note this is an async function, because the metadata (video duration) might not be available yet.
   */
  async getPlayerDurationForSlide(slide) {
    return this.getAttribute("autoplay") ?? 5;
  }
  /**
   * When the breakpoint changes (for instance from mobile to desktop), we may have to clean up the existing
   * attributes leave by Motion
   */
  cleanUpAnimations() {
    this.allCells.forEach((cell) => {
      cell.style.removeProperty("opacity");
      cell.style.removeProperty("visibility");
    });
  }

  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  async setupListeners() {
    if (this.hasAttribute("disabled-on")) {
      mediaQueryListener(this.getAttribute("disabled-on"), (event) => {
        if (event.matches) {
          this.#listenersAbortController?.abort();
          this.cleanUpAnimations();
        } else {
          this.setupListeners();
        }
      });
      if (matchesMediaQuery(this.getAttribute("disabled-on"))) {
        return;
      }
    }
    this.#listenersAbortController = new AbortController();
    const listenerOptions = { signal: this.#listenersAbortController.signal };
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => this.#preventInitialTransition = event.detail.load, listenerOptions);
    }
    if (this.allCells.length > 1) {
      this.addEventListener("carousel:change", this.preloadImages);
      if (this.allowSwipe) {
        this.#gestureArea = new GestureArea(this, { signal: this.#listenersAbortController.signal });
        this.addEventListener("swipeleft", this.next, listenerOptions);
        this.addEventListener("swiperight", this.previous, listenerOptions);
      }
      if (!this.hasAttribute("disable-keyboard-navigation")) {
        this.tabIndex = 0;
        this.addEventListener("keydown", this.onKeyboardNavigation, listenerOptions);
      }
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }), listenerOptions);
      }
      if (this.hasAttribute("autoplay")) {
        this.#player ??= new Player();
        this.#player.addEventListener("player:end", this.next.bind(this), listenerOptions);
        if (this.hasAttribute("pause-on-hover")) {
          this.addEventListener("mouseenter", () => this.#player.pause(), listenerOptions);
          this.addEventListener("mouseleave", () => this.#player.resume(), listenerOptions);
        }
        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", () => this.#player.stop(), listenerOptions);
          this.addEventListener("shopify:block:deselect", () => this.#player.start(), listenerOptions);
        }
      }
    }
  }
  onKeyboardNavigation(event) {
    if (event.target !== this) {
      return;
    }
    if (event.code === "ArrowLeft") {
      this.previous();
    } else if (event.code === "ArrowRight") {
      this.next();
    }
  }
  preloadImages() {
    const previousSlide = this.cells[(this.selectedIndex - 1 + this.cells.length) % this.cells.length], nextSlide = this.cells[(this.selectedIndex + 1 + this.cells.length) % this.cells.length];
    [previousSlide, this.selectedCell, nextSlide].forEach((item) => {
      Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
      Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
    });
  }
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}

// js/common/cart/fetch-cart.js
var createCartPromise = () => {
  return new Promise(async (resolve) => {
    resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
  });
};
var fetchCart = createCartPromise();
document.addEventListener("cart:change", (event) => {
  fetchCart = event.detail["cart"];
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    fetchCart = createCartPromise();
  }
});
document.addEventListener("cart:refresh", () => {
  fetchCart = createCartPromise();
});

// js/common/cart/cart-count.js
var CartCount = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
    this.updateFromServer = this.updateFromServer.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: this.#abortController.signal });
    document.addEventListener("cart:refresh", this.updateFromServer, { signal: this.#abortController.signal });
    window.addEventListener("pageshow", this.updateFromServer, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  set itemCount(count) {
    this.hidden = count === 0;
    this.querySelector("span").innerText = count;
  }
  async updateFromServer() {
    this.itemCount = (await fetchCart)["item_count"];
  }
};
if (!window.customElements.get("cart-count")) {
  window.customElements.define("cart-count", CartCount);
}

// js/common/cart/cart-discount.js
var AbstractCartDiscount = class extends HTMLElement {
  async getDiscountCodes() {
    return (await fetchCart)["discount_codes"].filter((discount) => discount.applicable).map((discount) => discount.code.toLowerCase());
  }
  async toggleDiscount(event) {
    let target = event.currentTarget;
    target.setAttribute("aria-busy", "true");
    let discountCodes = await this.getDiscountCodes();
    if (target.hasAttribute("discount-code")) {
      discountCodes = discountCodes.filter((discount2) => discount2 !== target.getAttribute("discount-code").toLowerCase());
    }
    let discount = (discountCodes.length > 0 ? discountCodes.join(",") + "," : "") + (event.target.value || "");
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ discount })
    });
    target.setAttribute("aria-busy", "false");
    if (!response.ok) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    }
    const responseJson = await response.json();
    if (responseJson.discount_codes.some((obj) => obj.applicable === false)) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    } else {
      this.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.reload();
    }
  }
};
var CartDiscountBanner = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
    this.onCartDiscountError = this.onCartDiscountError.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener("cart:discount:error", this.onCartDiscountError, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  onCartDiscountError() {
    this.hidden = false;
  }
};

var CartDiscountField = class extends AbstractCartDiscount {
  #hiddenDiscountInputOriginalValue;
  constructor() {
    super();
    this.updateHiddenInput = this.updateHiddenInput.bind(this);
    this.#hiddenDiscountInputOriginalValue = this.hiddenDiscountInput.value;
    this.addEventListener("change", this.toggleDiscount.bind(this));
    this.addEventListener("input", this.updateHiddenInput);
  }
  get hiddenDiscountInput() {
    return this.querySelector('[name="discount"]');
  }
  updateHiddenInput(event) {
    this.hiddenDiscountInput.value = [this.#hiddenDiscountInputOriginalValue, event.target.value].filter((val) => val && val.trim() !== "").join(",");
  }
};
var CartDiscountRemoveButton = class extends AbstractCartDiscount {
  constructor() {
    super();
    this.addEventListener("click", this.toggleDiscount.bind(this));
  }
};
if (!window.customElements.get("cart-discount-field")) {
  window.customElements.define("cart-discount-field", CartDiscountField);
}
if (!window.customElements.get("cart-discount-remove-button")) {
  window.customElements.define("cart-discount-remove-button", CartDiscountRemoveButton);
}
if (!window.customElements.get("cart-discount-banner")) {
  window.customElements.define("cart-discount-banner", CartDiscountBanner);
}

// js/common/cart/cart-note.js
var CartNote = class extends HTMLElement {
  constructor() {
    super();
    this.onNoteChanged = this.onNoteChanged.bind(this);
    this.addEventListener("change", this.onNoteChanged);
  }
  onNoteChanged(event) {
    if (event.target.getAttribute("name") !== "note") {
      return;
    }
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: event.target.value }),
      keepalive: true
      // Allows to make sure the request is fired even when submitting the form
    });
  }
};
if (!window.customElements.get("cart-note")) {
  window.customElements.define("cart-note", CartNote);
}

// js/common/cart/free-shipping-bar.js
var FreeShippingBar = class extends HTMLElement {
};
if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// js/common/cart/line-item.js
import { Delegate } from "vendor";
var LineItem = class extends HTMLElement {
  #delegate = new Delegate(this);
  constructor() {
    super();
    this.onQuantityChanged = this.onQuantityChanged.bind(this);
    this.onChangeLinkClicked = this.onChangeLinkClicked.bind(this);
    this.changeLineItemQuantity = this.changeLineItemQuantity.bind(this);
    this.#delegate.on("change", "[data-line-key]", this.onQuantityChanged);
    this.#delegate.on("click", `[href*="${Shopify.routes.root}cart/change"]`, this.onChangeLinkClicked);
  }
  onQuantityChanged(event, target) {
    this.changeLineItemQuantity(target.getAttribute("data-line-key"), parseInt(target.value));
  }
  onChangeLinkClicked(event, target) {
    event.preventDefault();
    const url = new URL(target.href);
    this.changeLineItemQuantity(url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
  }
  async changeLineItemQuantity(lineKey, targetQuantity) {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    const lineItem = this.closest("line-item");
    lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));
    let sectionsToBundle =  new Set();
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: targetQuantity,
        sections: [...sectionsToBundle].join(",")
      })
    });
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    if (!response.ok) {
      const responseContent = await response.json();
      const errorContainer = this.querySelector(".line-item__error-container");
      errorContainer.innerHTML = "";
      const errorSvg = `<svg width="13" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"></circle><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path></svg>`;
      errorContainer.insertAdjacentHTML("afterbegin", `<p class="h-stack gap-1.5 text-sm text-error" role="alert">${errorSvg} ${responseContent["description"]}</p>`);
      this.querySelector("quantity-selector")?.restoreDefaultValue();
    } else {
      const cartContent = await response.json();
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
      } else {
        const lineItemAfterChange = cartContent["items"].filter((lineItem2) => lineItem2["key"] === lineKey);
        lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
          bubbles: true,
          detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]["quantity"],
            cart: cartContent
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
      }
    }
  }
};
if (!window.customElements.get("line-item")) {
  window.customElements.define("line-item", LineItem);
}

// js/common/cart/shipping-estimator.js
var ShippingEstimator = class extends HTMLElement {
  #estimateShippingListener = this.estimateShipping.bind(this);
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.submitButton = this.querySelector('[type="button"]');
    this.resultsElement = this.querySelector('[aria-live="polite"]');
    this.submitButton.addEventListener("click", this.#estimateShippingListener);
  }
  disconnectedCallback() {
    this.submitButton.removeEventListener("click", this.#estimateShippingListener);
  }
  async estimateShipping(event) {
    event.preventDefault();
    const zip = this.querySelector('[name="address[zip]"]').value, country = this.querySelector('[name="address[country]"]').value, province = this.querySelector('[name="address[province]"]').value;
    this.submitButton.setAttribute("aria-busy", "true");
    const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
    if (prepareResponse.ok) {
      const shippingRates = await this.getAsyncShippingRates(zip, country, province);
      this.formatShippingRates(shippingRates);
    } else {
      const jsonError = await prepareResponse.json();
      this.formatError(jsonError);
    }
    this.resultsElement.hidden = false;
    this.submitButton.removeAttribute("aria-busy");
  }
  async getAsyncShippingRates(zip, country, province) {
    const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
    const responseAsText = await response.text();
    if (responseAsText === "null") {
      return this.getAsyncShippingRates(zip, country, province);
    } else {
      return JSON.parse(responseAsText)["shipping_rates"];
    }
  }
  formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>`}
      </div>
    `;
  }
  formatError(errors) {
    let formattedShippingRates = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-1">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="list-disc" role="list">${formattedShippingRates}</ul>
      </div>
    `;
  }
};
if (!window.customElements.get("shipping-estimator")) {
  window.customElements.define("shipping-estimator", ShippingEstimator);
}

// js/common/collection/collection-facets-floating-button.js
var CollectionFacetsFloatingButton = class extends SandwichVisibility {
  get startMarker() {
    return document.querySelector(".collection__toolbar");
  }
  get endMarker() {
    return document.querySelector(".shopify-section--footer");
  }
};
if (!window.customElements.get("collection-facets-floating-button")) {
  window.customElements.define("collection-facets-floating-button", CollectionFacetsFloatingButton);
}

// js/common/collection/layout-selector-button.js
var LayoutSelectorButton = class extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.addEventListener("change", this.onChange);
  }
  onChange(event) {
    let attributes = {};
    if (event.target.name === "desktop-layout") {
      attributes["product_card_desktop_layout"] = event.target.value;
    } else if (event.target.name === "mobile-items-per-row") {
      attributes["product_card_mobile_items_per_row"] = parseInt(event.target.value);
    }
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        attributes
      })
    });
    const productList = this.closest(".collection").querySelector(".product-list");
    if (event.target.name === "mobile-items-per-row") {
      productList.setAttribute("mobile-items-per-row", event.target.value);
    } else if (event.target.name === "desktop-layout") {
      productList.setAttribute("desktop-layout", event.target.value);
    }
    if (window.themeVariables.settings.staggerProducts) {
      this.closest(".collection").querySelector("product-list").reveal();
    }
  }
};
if (!window.customElements.get("collection-layout-selector-button")) {
  window.customElements.define("collection-layout-selector-button", LayoutSelectorButton);
}

// js/common/overlay/dialog-element.js
import { animate as animate4, FocusTrap, Delegate as Delegate2 } from "vendor";
var lockLayerCount = 0;
var DialogElement = class extends HTMLElement {
  #isLocked = false;
  #delegate = new Delegate2(document.body);
  #focusLeaveDelegate = new Delegate2(document.body);
  #abortController;
  #focusTrap;
  #isTransitioningToState;
  #originalParentBeforeAppend;
  constructor() {
    super();
    this.setAttribute("role", "dialog");
    if (this.shadowDomTemplate) {
      this.attachShadow({ mode: "open" }).appendChild(this.shadowDomTemplate.content.cloneNode(true));
      this.shadowRoot.addEventListener("slotchange", (event) => this.updateSlotVisibility(event.target));
    }
    this.onActivatorClicked = this.onActivatorClicked.bind(this);
    this.onActivatorPointerEnter = this.onActivatorPointerEnter.bind(this);
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  static get observedAttributes() {
    return ["open"];
  }
  connectedCallback() {
    if (this.id) {
      this.#delegate.off();
      this.#delegate.on("click", `[aria-controls="${this.id}"]`, this.onActivatorClicked);
      if (window.matchMedia("screen and (pointer: fine)").matches) {
        this.#delegate.on("pointerenter", `[aria-controls="${this.id}"][data-open-on-hover]`, this.onActivatorPointerEnter, true);
      }
    }
    this.#abortController = new AbortController();
    if (this.shadowDomTemplate) {
      this.getShadowPartByName("overlay")?.addEventListener("click", this.hide.bind(this), { signal: this.abortController.signal });
      Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => this.updateSlotVisibility(slot));
    }
    if (Shopify.designMode) {
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-editor-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show({ animate: !event.detail.load }), { signal: this.abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this.abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this.abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    this.#delegate.off();
    this.abortController.abort();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (this.#isLocked) {
      this.#isLocked = false;
      document.documentElement.classList.toggle("lock", --lockLayerCount > 0);
    }
  }
  /**
   * Open the dialog element (the animation can be disabled by passing false as an argument). It optionally accepts two parameters:
   *
   * animate: if set to false, the dialog will open immediately without any animation
   * activator: the activator that has been used to show the dialog (if any)
   */
  async show({ animate: animate25 = true, activator, conditionToFulfill } = {}) {
    if (this.#isTransitioningToState === "open" && this.open) {
      return Promise.resolve();
    }
    this.#isTransitioningToState = "open";
    this.controls.forEach((activator2) => activator2.setAttribute("aria-expanded", "true"));
    this.setAttribute("open", "");
    if (conditionToFulfill) {
      await conditionToFulfill;
    }
    this.#originalParentBeforeAppend = null;
    this.style.removeProperty("display");
    this.style.setProperty("display", this.shadowRoot ? "contents" : getComputedStyle(this).display);
    this.dispatchEvent(new CustomEvent("dialog:before-show", { detail: { activator } }));
    if (this.shouldAppendToBody && this.parentElement !== document.body) {
      this.#originalParentBeforeAppend = this.parentElement;
      document.body.append(this);
    }
    const animationControls = this.createEnterAnimationControls();
    if (!animate25) {
      animationControls.complete();
    }
    animationControls.then(() => {
      this.dispatchEvent(new CustomEvent("dialog:after-show", { detail: { activator } }));
      this.#isTransitioningToState = null;
    });
    if (this.shouldTrapFocus) {
      this.focusTrap.activate({
        checkCanFocusTrap: () => animationControls
      });
    }
    if (this.shouldLock) {
      lockLayerCount += 1;
      this.#isLocked = true;
      document.documentElement.classList.add("lock");
    }
    return animationControls;
  }
  /**
   * Hide the dialog element
   */
  hide() {
    if (this.#isTransitioningToState === "close" && !this.open) {
      return Promise.resolve();
    }
    this.#isTransitioningToState = "close";
    this.controls.forEach((activator) => activator.setAttribute("aria-expanded", "false"));
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("dialog:before-hide"));
    const hideTransitionPromise = this.createLeaveAnimationControls();
    hideTransitionPromise.then(() => {
      if (this.parentElement === document.body && this.#originalParentBeforeAppend) {
        if (document.getElementById(this.id) !== this) {
          return this.remove();
        }
        this.#originalParentBeforeAppend.appendChild(this);
        this.#originalParentBeforeAppend = null;
      }
      this.style.setProperty("display", "none");
      this.dispatchEvent(new CustomEvent("dialog:after-hide"));
      this.#isTransitioningToState = null;
    });
    this.focusTrap?.deactivate({
      checkCanReturnFocus: () => hideTransitionPromise
    });
    if (this.shouldLock) {
      this.#isLocked = false;
      document.documentElement.classList.toggle("lock", --lockLayerCount > 0);
    }
    return hideTransitionPromise;
  }
  /**
   * Get the abort controller used to clean listeners. You can retrieve it in children classes to add your own listeners
   * that will be cleaned when the element is removed or re-rendered
   */
  get abortController() {
    return this.#abortController;
  }
  /**
   * Get all the elements controlling this dialog (typically, button). An element controls this dialog if it has an
   * aria-controls attribute matching the ID of this dialog element
   */
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  /**
   * Returns if the dialog is open or closed
   */
  get open() {
    return this.hasAttribute("open");
  }
  /**
   * If true is returned, then FocusTrap will activate and manage all the focus management. This is required for good
   * accessibility (such as keyboard management) and should normally not be set to false in children classes unless
   * there is a very good reason to do so
   */
  get shouldTrapFocus() {
    return true;
  }
  /**
   * When the dialog focus is trapped, define if the page is lock (not scrollable). This is usually desirable on
   * full screen modals
   */
  get shouldLock() {
    return false;
  }
  /**
   * By default, when the focus is trapped on an element, a click outside the trapped element close it. Sometimes, it
   * may be desirable to turn off all interactions so that all clicks outside don't do anything
   */
  get clickOutsideDeactivates() {
    return true;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return this.hasAttribute("append-to-body") ? true : false;
  }
  /**
   * Decide which element to focus first when the dialog focus is trapped. By default, the first focusable element
   * will be focused, but this can be overridden by passing a selector in the "initial-focus" attribute
   */
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") === "false" ? false : this.querySelector(this.getAttribute("initial-focus")) : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  /**
   * If set to true, then focus trap will not automatically scroll to the first focused element, which can cause
   * annoying experience.
   */
  get preventScrollWhenTrapped() {
    return true;
  }
  /**
   * Allow custom elements to define their own, custom trap stack. If none is defined, the default one will be used
   */
  get trapStack() {
    return null;
  }
  /**
   * If set to true, dialog can be close by clicking "escape"
   */
  get escapeDeactivates() {
    return true;
  }
  /**
   * Get the focus trap element configured with all the other attributes
   */
  get focusTrap() {
    return this.#focusTrap = this.#focusTrap || new FocusTrap.createFocusTrap(this, {
      onDeactivate: this.hide.bind(this),
      allowOutsideClick: this.clickOutsideDeactivates ? this.allowOutsideClick.bind(this) : false,
      initialFocus: matchesMediaQuery("supports-hover") ? this.initialFocus : false,
      fallbackFocus: this,
      preventScroll: this.preventScrollWhenTrapped,
      escapeDeactivates: this.escapeDeactivates,
      tabbableOptions: {
        getShadowRoot: true
      },
      trapStack: this.trapStack
    });
  }
  /**
   * Get the ShadowDOM template (if any). If there is one defined, the dialog automatically constructs it with the
   * shadow DOM. It will first attempt to check the "template" attribute and, if none is found, will fallback with
   * the first template children tag
   */
  get shadowDomTemplate() {
    if (this.hasAttribute("template")) {
      return document.getElementById(this.getAttribute("template"));
    }
    return this.querySelector(":scope > template");
  }
  /**
   * For dialog that use Shadow DOM, this allows a quick retrieval of parts by name
   */
  getShadowPartByName(name) {
    return this.shadowRoot?.querySelector(`[part="${name}"]`);
  }
  /**
   * Callback called when attributes changes. To show/hide the dialog, you should use exclusively the "show" and "hide"
   * methods. However, the theme might insert dialog that we want to be pre-opened (for instance if it has an error). To
   * do that, we monitor the "open" attribute and open the dialog if it is set
   */
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        if (oldValue === null && newValue === "") {
          this.show();
        } else if (oldValue !== null && newValue === null) {
          this.hide();
        }
        break;
    }
  }
  /**
   * Create the animation controls for the enter animation
   */
  createEnterAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * Create the animation controls for the leave animation
   */
  createLeaveAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * When "clickOutsideDeactivates" is true, this method is called on the final click destination. If this method
   * returns true, then the dialog closes (if false, the dialog remains in its current state). By default, this
   * will close the dialog if a click is done outside the dialog. However, this may be overridden in children classes
   * to provide custom behavior (for instance, to only allow some elements to close the dialog)
   */
  hideForOutsideClickTarget(target) {
    if (this.controls.includes(target) && target.hasAttribute("data-open-on-hover")) {
      return false;
    }
    return !this.contains(target);
  }
  /**
   * When "clickOutsideDeactivates" is set to true, this method allows to control which element, when clicked, allows
   * to pass-through and have its behavior being executed
   */
  allowOutsideClickForTarget(target) {
    if (this.controls.includes(target) && target.hasAttribute("data-open-on-hover")) {
      return true;
    }
    return false;
  }
  /**
   * If "clickOutsideDeactivates" is true, then this listener will be called on every click outside the element. This
   * allows function separates touch and non-touch events
   */
  allowOutsideClick(event) {
    if ("TouchEvent" in window && event instanceof TouchEvent) {
      return this.allowOutsideClickTouch(event);
    } else {
      return this.allowOutsideClickMouse(event);
    }
  }
  /**
   * If "clickOutsideDeactivates" is true, this listener will be called on every touch click outside the trapped
   * element. By default, this will allow any click outside to cause the dialog to close
   */
  allowOutsideClickTouch(event) {
    event.target.addEventListener("touchend", (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (this.hideForOutsideClickTarget(endTarget)) {
        this.hide();
      }
    }, { once: true, signal: this.abortController.signal });
    return this.allowOutsideClickForTarget(event.target);
  }
  /**
   * If "clickOutsideDeactivates" is true, this listener will be called on every mouse click outside the trapped
   * element. By default, this will allow any click outside to cause the dialog to close.
   */
  allowOutsideClickMouse(event) {
    if (event.type !== "click") {
      return false;
    }
    if (this.hideForOutsideClickTarget(event.target)) {
      this.hide();
    }
    if (this.allowOutsideClickForTarget(event.target)) {
      return true;
    }
    let target = event.target, closestControl = event.target.closest("[aria-controls]");
    if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
      target = closestControl;
    }
    return this.id !== target.getAttribute("aria-controls");
  }
  /**
   * This function is called whenever an activator (an element controlling this dialog) is called. This simply open
   * the dialog if closed, or close it if open
   */
  onActivatorClicked(event, activator) {
    if (this.open && this.controls.includes(event.target) && event.target.tagName === "A" && event.target.hasAttribute("data-open-on-hover")) {
      return;
    }
    event?.preventDefault();
    this.open ? this.hide() : this.show({ activator });
  }
  /**
   * This function is called whenever an activator with the attribute "data-open-on-hover" is hovered.
   */
  onActivatorPointerEnter() {
    if (!this.open) {
      this.show();
      this.#focusLeaveDelegate.off().on("pointerenter", (event) => {
        if (event.target.contains(this) || this.contains(event.target) || event.target.getAttribute("aria-controls") === this.id || event.target.closest("[aria-controls]")?.getAttribute("aria-controls") === this.id) {
          return;
        }
        this.#focusLeaveDelegate.off();
        this.hide();
      }, true);
    }
  }
  /**
   * Hide the slots that do not have any children
   */
  updateSlotVisibility(slot) {
    if (!["header", "footer"].includes(slot.name)) {
      return;
    }
    slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
  }
};
var DialogCloseButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("dialog-close-button")) {
  window.customElements.define("dialog-close-button", DialogCloseButton);
}

// js/common/overlay/drawer.js
import { animateSequence as animateSequence2 } from "vendor";
var Drawer = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
  }
  get shadowDomTemplate() {
    return document.getElementById(this.getAttribute("template") || "drawer-default-template");
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  get openFrom() {
    return this.getAttribute("open-from") || "right";
  }
  createEnterAnimationControls() {
    let contentTransform = this.openFrom === "right" ? ["translateX(calc(var(--transform-logical-flip) * 100%)", "translateX(0)"] : ["translateX(calc(-1 * var(--transform-logical-flip) * 100%)", "translateX(0)"];
    this.getShadowPartByName("content").style.marginInlineStart = this.openFrom === "right" ? "auto" : "0";
    return animateSequence2([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.25, ease: "easeInOut" }],
      [this.getShadowPartByName("content"), { opacity: [0, 1], transform: contentTransform }, { duration: 0.45, at: "-0.15", ease: [0.86, 0, 0.07, 1] }]
    ]);
  }
  createLeaveAnimationControls() {
    let contentTransform = this.openFrom === "right" ? ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * 100%)"] : ["translateX(0)", "translateX(calc(-1 * var(--transform-logical-flip) * 100%)"];
    return animateSequence2([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.45, ease: "easeInOut" }],
      [this.getShadowPartByName("content"), { opacity: [1, 0], transform: contentTransform }, { duration: 0.45, at: "<", ease: [0.86, 0, 0.07, 1] }]
    ]);
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

// js/common/overlay/drawer-popover.js
import { animateSequence as animateSequence3 } from "vendor";
var DrawerPopover = class extends DialogElement {
  get shadowDomTemplate() {
    return document.getElementById(this.getAttribute("template") || "drawer-popover-default-template");
  }
  createEnterAnimationControls() {
    return animateSequence3([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.25, ease: "easeInOut" }],
      [this.getShadowPartByName("content"), { opacity: [0, 1], transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.45, at: "-0.15", ease: [0.86, 0, 0.07, 1] }]
    ]);
  }
  createLeaveAnimationControls() {
    return animateSequence3([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.45, ease: "easeInOut" }],
      [this.getShadowPartByName("content"), { opacity: [1, 0], transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.45, at: "<", ease: [0.86, 0, 0.07, 1] }]
    ]);
  }
};
if (!window.customElements.get("x-drawer-popover")) {
  window.customElements.define("x-drawer-popover", DrawerPopover);
}

// js/common/overlay/modal.js
import { animateSequence as animateSequence4 } from "vendor";
var Modal = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
  }
  get shouldLock() {
    return true;
  }
  get shadowDomTemplate() {
    return document.getElementById("modal-default-template");
  }
  createEnterAnimationControls() {
    return animateSequence4([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.25 }],
      [this.getShadowPartByName("content"), { opacity: [0, 1], transform: ["translateY(40px)", "translateY(0)"] }, { duration: 0.25, at: "-0.15" }]
    ]);
  }
  createLeaveAnimationControls() {
    return animateSequence4([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.15 }],
      [this.getShadowPartByName("content"), { opacity: [1, 0], transform: ["translateY(0)", "translateY(40px)"] }, { duration: 0.15, at: "<" }]
    ]);
  }
};
if (!window.customElements.get("x-modal")) {
  window.customElements.define("x-modal", Modal);
}

// js/common/overlay/popover.js
import { animate as animate5, animateSequence as animateSequence5, Delegate as Delegate3 } from "vendor";
var Popover = class extends DialogElement {
  #delegate = new Delegate3(this);
  connectedCallback() {
    super.connectedCallback();
    this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
    this.addEventListener("dialog:after-hide", this.#resetPopoverStyles, { signal: this.abortController.signal });
    this.addEventListener("dialog:after-show", this.#commitPopoverStyles, { signal: this.abortController.signal });
    window.matchMedia(window.themeVariables.mediaQueries["md-max"]).addEventListener("change", this.#onPopoverBreakpointChange, { signal: this.abortController.signal });
    if (this.hasAttribute("close-on-listbox-change")) {
      this.addEventListener("change", this.hide, { signal: this.abortController.signal });
      this.#delegate.off();
      this.#delegate.on("click", "a", this.hide.bind(this));
      this.#delegate.on("click", "input:checked", this.hide.bind(this));
    }
  }
  #resetPopoverStyles = () => {
    ["base", "overlay", "content"].forEach((partName) => {
      const part = this.getShadowPartByName(partName);
      if (!part) {
        return;
      }
      part.getAnimations().forEach((animation) => animation.cancel());
      part.style.cssText = "";
    });
  };
  #commitPopoverStyles = () => {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    if (!content) {
      return;
    }
    if (matchesMediaQuery("md-max")) {
      if (overlay) {
        overlay.style.opacity = "1";
      }
      content.style.opacity = "1";
      content.style.transform = "translateY(0)";
    } else {
      content.style.opacity = "1";
      content.style.transform = "translateY(0)";
    }
  };
  #ensurePopoverParent() {
    if (this.open) {
      return;
    }
    const targetParent = this.controls[0]?.parentElement;
    if (targetParent && this.parentElement !== targetParent) {
      targetParent.appendChild(this);
    }
  }
  #onPopoverBreakpointChange = () => {
    this.#resetPopoverStyles();
    if (this.open) {
      this.hide();
    }
    this.#ensurePopoverParent();
  };
  #primeMobileEnterStyles() {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    if (overlay) {
      overlay.style.opacity = "0";
    }
    if (content) {
      content.style.opacity = "1";
      content.style.transform = "translateY(100%)";
    }
  }
  async show(options = {}) {
    this.#ensurePopoverParent();
    this.#resetPopoverStyles();
    if (matchesMediaQuery("md-max")) {
      this.#primeMobileEnterStyles();
    }
    return super.show(options);
  }
  hide() {
    const animationControls = super.hide();
    Promise.resolve(animationControls).then(() => this.#resetPopoverStyles());
    return animationControls;
  }
  get shadowDomTemplate() {
    return document.getElementById(this.getAttribute("template") || "popover-default-template");
  }
  get shouldLock() {
    return matchesMediaQuery("md-max");
  }
  get shouldAppendToBody() {
    return matchesMediaQuery("md-max");
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  createEnterAnimationControls() {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    if (matchesMediaQuery("md-max")) {
      this.#primeMobileEnterStyles();
      return animateSequence5([
        [overlay, { opacity: [0, 1] }, { duration: 0.3, ease: [0.645, 0.045, 0.355, 1] }],
        [content, { opacity: 1, transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.3, at: "<", ease: [0.645, 0.045, 0.355, 1] }]
      ]);
    } else {
      if (content) {
        content.style.transform = "translateY(0)";
        content.style.opacity = "0";
      }
      return animate5(content, { opacity: [0, 1], transform: ["translateY(0)", "translateY(0)"] }, { duration: 0.3, ease: "easeInOut" });
    }
  }
  createLeaveAnimationControls() {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    let animationControls;
    if (matchesMediaQuery("md-max")) {
      animationControls = animateSequence5([
        [overlay, { opacity: [1, 0] }, { duration: 0.3, ease: [0.645, 0.045, 0.355, 1] }],
        [content, { opacity: 1, transform: ["translateY(0%)", "translateY(100%)"] }, { duration: 0.3, at: "<", ease: [0.645, 0.045, 0.355, 1] }]
      ]);
    } else {
      animationControls = animate5(content, { opacity: [1, 0], transform: ["translateY(0)", "translateY(0)"] }, { duration: 0.3, ease: "easeInOut" });
    }
    Promise.resolve(animationControls).then(() => this.#resetPopoverStyles());
    return animationControls;
  }
};
if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}

// js/common/facets/facets-drawer.js
var FacetsDrawer = class extends Drawer {
  constructor() {
    super();
    this.addEventListener("submit", this.hide);
    this.addEventListener("reset", this.hide);
    this.addEventListener("facet:update", this.hide);
  }
};
if (!window.customElements.get("facets-drawer")) {
  window.customElements.define("facets-drawer", FacetsDrawer);
}

// js/common/facets/facets-form.js
var FacetsForm = class extends HTMLElement {
  #isDirty = false;
  constructor() {
    super();
    this.onFormChanged = this.onFormChanged.bind(this);
    this.onFormSubmitted = this.onFormSubmitted.bind(this);
    this.onFormResetted = this.onFormResetted.bind(this);
    this.addEventListener("change", this.onFormChanged);
    this.addEventListener("submit", this.onFormSubmitted, { capture: true });
    this.addEventListener("reset", this.onFormResetted);
  }
  get form() {
    return this.querySelector("form");
  }
  buildUrl({ clearParams = false } = {}) {
    const searchParams = new URLSearchParams(new FormData(this.form)), url = new URL(this.form.action);
    url.search = "";
    if (!clearParams) {
      searchParams.forEach((value, key) => url.searchParams.append(key, value));
      ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
        if (url.searchParams.get(optionToClear) === "") {
          url.searchParams.delete(optionToClear);
        }
      });
    }
    url.searchParams.set("section_id", this.getAttribute("section-id"));
    return url;
  }
  onFormChanged() {
    this.#isDirty = true;
    if (this.hasAttribute("update-on-change")) {
      if (HTMLFormElement.prototype.requestSubmit) {
        this.form.requestSubmit();
      } else {
        this.form.dispatchEvent(new Event("submit", { bubbles: true }));
      }
    } else {
      cachedFetch(this.buildUrl().toString());
    }
  }
  onFormSubmitted(event) {
    event.preventDefault();
    if (!this.#isDirty) {
      return;
    }
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: this.buildUrl(),
        scrollTo: this.form.getAttribute("scroll-to")
      }
    }));
    this.#isDirty = false;
  }
  onFormResetted(event) {
    event.preventDefault();
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: this.buildUrl({ clearParams: true })
      }
    }));
    this.#isDirty = false;
  }
};
if (!window.customElements.get("facets-form")) {
  window.customElements.define("facets-form", FacetsForm);
}

// js/common/facets/facet-link.js
var FacetLink = class extends HTMLElement {
  constructor() {
    super();
    this.onFacetUpdate = this.onFacetUpdate.bind(this);
    this.addEventListener("click", this.onFacetUpdate);
  }
  onFacetUpdate(event) {
    event.preventDefault();
    const sectionId = extractSectionId(event.target), url = new URL(this.firstElementChild.href);
    url.searchParams.set("section_id", sectionId);
    if (this.hasAttribute("merge-params")) {
      const existingParams = new URLSearchParams(window.location.search);
      for (const [key, value] of url.searchParams) {
        existingParams.set(key, value);
      }
      url.search = existingParams.toString();
    }
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url,
        scrollTo: url.hash,
        disableCache: this.hasAttribute("disable-cache"),
        ignoreUrlSearch: this.hasAttribute("ignore-url-search")
      }
    }));
  }
};
if (!window.customElements.get("facet-link")) {
  window.customElements.define("facet-link", FacetLink);
}

// js/common/facets/facets-listeners.js
import { Delegate as Delegate4 } from "vendor";
var abortController = null;
var delegate = new Delegate4(document.body);
var openDetailsValues = new Set(Array.from(document.querySelectorAll('facets-form details[open] input[name*="filter."]'), (item) => item.name));
delegate.on("toggle", "facets-form details", (event, detailsElement) => {
  const inputNames = [...new Set(Array.from(detailsElement.querySelectorAll('input[name*="filter."]'), (item) => item.name))];
  inputNames.forEach((inputName) => {
    detailsElement.open ? openDetailsValues.add(inputName) : openDetailsValues.delete(inputName);
  });
}, true);
document.addEventListener("facet:update", async (event) => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const url = event.detail.url, shopifySection = document.getElementById(`shopify-section-${url.searchParams.get("section_id")}`);
  const clonedUrl = new URL(url);
  clonedUrl.searchParams.delete("section_id");
  clonedUrl.hash = "";
  if (event.detail.ignoreUrlSearch) {
    clonedUrl.search = "";
  }
  history.replaceState({}, "", clonedUrl.toString());
  try {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let tempContent;
    if (event.detail.disableCache) {
      tempContent = new DOMParser().parseFromString(await (await fetch(url.toString(), { signal: abortController.signal })).text(), "text/html");
    } else {
      tempContent = new DOMParser().parseFromString(await (await cachedFetch(url.toString(), { signal: abortController.signal })).text(), "text/html");
    }
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    const newShopifySection = tempContent.querySelector(".shopify-section");
    newShopifySection.querySelectorAll("facets-form details").forEach((detailsElement) => {
      const inputNames = [...new Set(Array.from(detailsElement.querySelectorAll('input[name*="filter."]'), (item) => item.name))];
      inputNames.forEach((inputName) => {
        detailsElement.toggleAttribute("open", openDetailsValues.has(inputName));
      });
    });
    const focusedElement = document.activeElement;
    shopifySection.replaceChildren(...document.importNode(tempContent.querySelector(".shopify-section"), true).childNodes);
    if (focusedElement?.id && document.getElementById(focusedElement.id)) {
      document.getElementById(focusedElement.id).focus();
    }
    const scrollToProductList = () => shopifySection.querySelector(event.detail.scrollTo)?.scrollIntoView({ block: "start", behavior: "smooth" });
    if ("requestIdleCallback" in window) {
      requestIdleCallback(scrollToProductList, { timeout: 500 });
    } else {
      requestAnimationFrame(scrollToProductList);
    }
  } catch (e) {
  }
});

// js/common/facets/sort-by-popover.js
var SortByPopover = class extends Popover {
  get shouldAppendToBody() {
    return false;
  }
};
if (!window.customElements.get("sort-by-popover")) {
  window.customElements.define("sort-by-popover", SortByPopover);
}

// js/common/feedback/progress-bar.js
import { inView as inView5, scroll } from "vendor";
var progressValuesMapping =  new Map();
var ProgressBar = class extends HTMLElement {
  #allowUpdatingProgress;
  constructor() {
    super(...arguments);
    this.setupTargetTracking = this.setupTargetTracking.bind(this);
    this.animateProgress = this.animateProgress.bind(this);
    this.#allowUpdatingProgress = !this.hasAttribute("animate-on-scroll") || this.hasAttribute("track-scroll-target");
  }
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  connectedCallback() {
    if (this.hasAttribute("track-scroll-target")) {
      inView5(this, this.setupTargetTracking);
    } else {
      if (this.hasAttribute("animate-on-scroll")) {
        inView5(this, () => {
          this.#allowUpdatingProgress = true;
          this.animateProgress();
        });
      } else {
        this.animateProgress();
      }
    }
  }
  disconnectedCallback() {
    if (this.id) {
      progressValuesMapping.set(this.id, this.progress);
    }
  }
  get progress() {
    return Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"));
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    if (!this.hasAttribute("track-scroll-target")) {
      this.animateProgress();
    }
  }
  setupTargetTracking() {
    const scrollTarget = document.getElementById(this.getAttribute("track-scroll-target"));
    const initialValue = scrollTarget.clientWidth / scrollTarget.scrollWidth;
    scroll((progress) => {
      this.style.setProperty("--progress-bar-progress", initialValue + Math.abs(progress) * (1 - initialValue));
    }, {
      container: scrollTarget,
      axis: "x"
    });
  }
  animateProgress() {
    if (!this.#allowUpdatingProgress) {
      return;
    }
    let previousValue = progressValuesMapping.get(this.id);
    this.animate({
      "--progress-bar-progress": previousValue ? [previousValue, this.progress] : this.progress
    }, {
      duration: 450,
      easing: "ease-out",
      fill: "both"
    });
    progressValuesMapping.delete(this.id);
  }
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}

// js/common/feedback/toast.js
import { animate as animate6 } from "vendor";
var Toast = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.onShow = this.onShow.bind(this);
  }
  connectedCallback() {
    document.addEventListener("toast:show", this.onShow);
  }
  onShow(event) {
    const messageFragment = document.createRange().createContextualFragment(`
      <div class="toast__message ${event.detail?.tone === "error" ? "toast__message--error" : ""}" role="${event.detail?.tone === "error" ? "alert" : "status"}" aria-live="polite">
        ${event.detail.message}
      </div>
    `);
    const messageElement = messageFragment.firstElementChild;
    this.replaceChildren(messageElement);
    animate6(messageElement, { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { duration: 0.25, ease: "easeInOut" });
    setTimeout(async () => {
      await animate6(messageElement, { opacity: [1, 0], transform: ["scale(1)", "scale(0.85)"] }, { duration: 0.25, ease: "easeInOut" });
      messageElement.remove();
    }, event.detail?.duration || 2500);
  }
};
if (!window.customElements.get("x-toast")) {
  window.customElements.define("x-toast", Toast);
}

// js/common/form/price-range.js
var PriceRange = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    this.#abortController = new AbortController();
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child'), rangeHigherBound = this.querySelector('input[type="range"]:last-child'), textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]'), textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    textInputLowerBound.addEventListener("focus", () => textInputLowerBound.select(), { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("focus", () => textInputHigherBound.select(), { signal: this.#abortController.signal });
    textInputLowerBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1), event.target.min);
      rangeLowerBound.value = event.target.value;
      rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(rangeLowerBound.value) / parseInt(rangeLowerBound.max) * 100}%`);
    }, { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1), event.target.max);
      rangeHigherBound.value = event.target.value;
      rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(rangeHigherBound.value) / parseInt(rangeHigherBound.max) * 100}%`);
    }, { signal: this.#abortController.signal });
    rangeLowerBound.addEventListener("change", (event) => {
      event.stopPropagation();
      textInputLowerBound.value = event.target.value;
      textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this.#abortController.signal });
    rangeHigherBound.addEventListener("change", (event) => {
      event.stopPropagation();
      textInputHigherBound.value = event.target.value;
      textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this.#abortController.signal });
    rangeLowerBound.addEventListener("input", (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputLowerBound.value = event.target.value;
    }, { signal: this.#abortController.signal });
    rangeHigherBound.addEventListener("input", (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputHigherBound.value = event.target.value;
    }, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

// js/common/form/quantity-selector.js
var QuantitySelector = class extends HTMLElement {
  #abortController;
  #decreaseButton;
  #increaseButton;
  #inputElement;
  constructor() {
    super(...arguments);
    this.onDecreaseQuantity = this.onDecreaseQuantity.bind(this);
    this.onIncreaseQuantity = this.onIncreaseQuantity.bind(this);
    this.updateUI = this.updateUI.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    this.#decreaseButton = this.querySelector("button:first-of-type");
    this.#increaseButton = this.querySelector("button:last-of-type");
    this.#inputElement = this.querySelector("input");
    this.#decreaseButton?.addEventListener("click", this.onDecreaseQuantity, { signal: this.#abortController.signal });
    this.#increaseButton?.addEventListener("click", this.onIncreaseQuantity, { signal: this.#abortController.signal });
    this.#inputElement?.addEventListener("input", this.updateUI, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  get quantity() {
    return this.#inputElement.value;
  }
  set quantity(quantity) {
    this.#inputElement.value = quantity;
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.updateUI();
  }
  restoreDefaultValue() {
    this.#inputElement.value = this.#inputElement.defaultValue;
    this.updateUI();
  }
  onDecreaseQuantity() {
    if (this.hasAttribute("allow-reset-to-zero") && this.#inputElement.value === this.#inputElement.min) {
      this.#inputElement.value = 0;
    } else {
      this.#inputElement.stepDown();
    }
    announceStatus(this.#inputElement.value);
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.updateUI();
  }
  onIncreaseQuantity() {
    this.#inputElement.stepUp();
    announceStatus(this.#inputElement.value);
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.updateUI();
  }
  updateUI() {
    if (this.#decreaseButton) {
      this.#decreaseButton.disabled = parseInt(this.#inputElement.value) <= parseInt(this.#inputElement.min);
    }
    if (this.#increaseButton) {
      this.#increaseButton.disabled = this.#inputElement.hasAttribute("max") ? parseInt(this.#inputElement.value) >= parseInt(this.#inputElement.max) : false;
    }
  }
};
var QuantityInput = class extends HTMLElement {
  constructor() {
    super();
    this.onValueInput = this.onValueInput.bind(this);
    this.onValueChange = this.onValueChange.bind(this);
    this.inputElement.addEventListener("input", this.onValueInput);
    this.inputElement.addEventListener("change", this.onValueChange);
    this.inputElement.addEventListener("focus", () => this.inputElement.select());
  }
  connectedCallback() {
    this.style.setProperty("--quantity-selector-character-count", `${this.inputElement.value.length}ch`);
  }
  get quantity() {
    return parseInt(this.inputElement.value);
  }
  get inputElement() {
    return this.firstElementChild;
  }
  onValueInput() {
    if (this.inputElement.value === "") {
      this.inputElement.value = this.inputElement.min || 1;
    }
    this.style.setProperty("--quantity-selector-character-count", `${this.inputElement.value.length}ch`);
  }
  onValueChange() {
    if (!this.inputElement.checkValidity()) {
      this.inputElement.stepDown();
    }
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput);
}

// js/common/product/gift-card-recipient.js
var GiftCardRecipient = class extends HTMLElement {
  #recipientCheckbox;
  #recipientOtherProperties = [];
  #recipientSendOnProperty;
  #offsetProperty;
  #recipientFieldsContainer;
  constructor() {
    super(...arguments);
    this.synchronizeProperties = this.synchronizeProperties.bind(this);
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    this.#recipientCheckbox = properties.find((input) => input.name === checkboxPropertyName);
    this.#recipientOtherProperties = properties.filter((input) => input.name !== checkboxPropertyName);
    this.#recipientFieldsContainer = this.querySelector(".gift-card-recipient__fields");
    this.#offsetProperty = this.querySelector('[name="properties[__shopify_offset]"]');
    if (this.#offsetProperty) {
      this.#offsetProperty.value = ( new Date()).getTimezoneOffset().toString();
    }
    this.#recipientSendOnProperty = this.querySelector('[name="properties[Send on]"]');
    const minDate =  new Date();
    const maxDate =  new Date();
    maxDate.setDate(minDate.getDate() + 90);
    this.#recipientSendOnProperty?.setAttribute("min", this.formatDate(minDate));
    this.#recipientSendOnProperty?.setAttribute("max", this.formatDate(maxDate));
    this.#recipientCheckbox?.addEventListener("change", this.synchronizeProperties);
    this.synchronizeProperties();
  }
  synchronizeProperties() {
    this.#recipientOtherProperties.forEach((property) => property.disabled = !this.#recipientCheckbox.checked);
    this.#recipientFieldsContainer.toggleAttribute("hidden", !this.#recipientCheckbox.checked);
  }
  formatDate(date) {
    const offset = date.getTimezoneOffset();
    const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
    return offsetDate.toISOString().split("T")[0];
  }
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}

// js/common/product/product-card.js
import { Delegate as Delegate5 } from "vendor";
var ProductCard = class extends HTMLElement {
  #delegate = new Delegate5(this);
  constructor() {
    super(...arguments);
    this.onSwatchHovered = this.onSwatchHovered.bind(this);
    this.onSwatchChanged = this.onSwatchChanged.bind(this);
    this.createMediaImg = this.createMediaImg.bind(this);
  }
  connectedCallback() {
    this.#delegate.on("change", '.product-card__swatch-list [type="radio"]', this.onSwatchChanged);
    this.#delegate.on("pointerover", '.product-card__swatch-list [type="radio"] + label', this.onSwatchHovered, true);
  }
  disconnectedCallback() {
    this.#delegate.off();
  }
  async onSwatchHovered(event, target) {
    const control = target.control;
    const primaryMediaElement = this.querySelector(".product-card__image--primary");
    if (control.hasAttribute("data-variant-media")) {
      this.createMediaImg(JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
    }
  }
  async onSwatchChanged(event, target) {
    if (target.hasAttribute("data-product-url")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
        link.href = target.getAttribute("data-product-url");
      });
      this.querySelector("quick-buy-modal")?.setAttribute("product-url", target.getAttribute("data-product-url"));
    } else if (target.hasAttribute("data-variant-id")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set("variant", target.getAttribute("data-variant-id"));
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
      const quickBuyModal = this.querySelector("quick-buy-modal");
      if (quickBuyModal) {
        const url = quickBuyModal.getAttribute("product-url").split("?")[0];
        quickBuyModal.setAttribute("product-url", `${url}?variant=${target.getAttribute("data-variant-id")}`);
      }
    }
    if (!target.hasAttribute("data-variant-media")) {
      return;
    }
    let newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".product-card__image--primary"), secondaryMediaElement = this.querySelector(".product-card__image--secondary"), newPrimaryMediaElement = this.createMediaImg(newMedia, primaryMediaElement.className, primaryMediaElement.sizes), newSecondaryMediaElement = null;
    if (target.hasAttribute("data-variant-secondary-media")) {
      let newSecondaryMedia = JSON.parse(target.getAttribute("data-variant-secondary-media"));
      newSecondaryMediaElement = this.createMediaImg(newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
      newSecondaryMediaElement.ariaHidden = "true";
    }
    if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
      if (secondaryMediaElement && newSecondaryMediaElement) {
        secondaryMediaElement.replaceWith(newSecondaryMediaElement);
      }
      const shouldTransition = target.closest(".product-card__info") !== null;
      if (shouldTransition) {
        await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: "ease-in", fill: "forwards" }).finished;
        await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
      }
      primaryMediaElement.replaceWith(newPrimaryMediaElement);
      if (shouldTransition) {
        newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: "ease-in" });
      }
    }
  }
  createMediaImg(media, className, sizes) {
    return createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: className, sizes });
  }
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}

// js/common/product/product-form.js
var ProductForm = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
    this.onSubmit = this.onSubmit.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (this.form) {
      this.form.addEventListener("submit", this.onSubmit, { signal: this.#abortController.signal });
      this.form.id.disabled = false;
    }
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  get form() {
    return this.querySelector('form[action*="/cart/add"]');
  }
  async onSubmit(event) {
    event.preventDefault();
    if (event.submitter?.getAttribute("aria-busy") === "true") {
      return;
    }
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    const showLoadingBar = event.submitter?.querySelector("button-content") === null;
    const submitButtons = Array.from(this.form.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });
    let sectionsToBundle =  new Set();
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const formData = new FormData(this.form);
    formData.set("sections", [...sectionsToBundle].join(","));
    if (showLoadingBar) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    }
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
        // Needed for Shopify to check inventory
      }
    });
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    const responseJson = await response.json();
    if (showLoadingBar) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent["sections"] = responseJson["sections"];
      const items = responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson];
      this.form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items,
          cart: cartContent
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      }));
      if (window.themeVariables.settings.cartType === "message") {
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: {
            message: window.themeVariables.strings.addedToCart.replace("{{product_title}}", items?.[0]?.title)
          }
        }));
      } else if (window.themeVariables.settings.cartType === "drawer") {
        let content = [];
        items.forEach((item) => {
          content.push(window.themeVariables.strings.addedToCart.replace("{{product_title}}", item?.title || ""));
        });
        announceStatus(content.join(", "));
      }
    } else {
      this.form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["message"]
        }
      }));
      document.dispatchEvent(new CustomEvent("toast:show", {
        detail: {
          message: responseJson["message"],
          tone: "error"
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm);
}

// js/common/product/product-form-listeners.js
var BuyButtons = class extends HTMLElement {
  connectedCallback() {
  }
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}

// js/common/product/product-gallery.js
import { PhotoSwipeLightbox } from "vendor";
var ProductGallery = class extends HTMLElement {
  #abortController;
  #photoSwipeInstance;
  #settledMedia;
  #onGestureChangedListener = this.onGestureChanged.bind(this);
  /* Keep track of the currently settled media */
  constructor() {
    super();
    this.registerLightboxUi = this.registerLightboxUi.bind(this);
    this.onSectionRerender = this.onSectionRerender.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onMediaSettle = this.onMediaSettle.bind(this);
    this.onGestureStart = this.onGestureStart.bind(this);
    this.addEventListener("lightbox:open", (event) => this.openLightBox(event?.detail?.index));
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (!this.carousel) {
      return;
    }
    const form = document.forms[this.getAttribute("form")];
    form.addEventListener("product:rerender", this.onSectionRerender, { signal: this.#abortController.signal });
    form.addEventListener("variant:change", this.onVariantChange, { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:change", this.onMediaChange, { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:settle", this.onMediaSettle, { signal: this.#abortController.signal });
    if (this.hasAttribute("allow-zoom")) {
      this.carousel.addEventListener("gesturestart", this.onGestureStart, { capture: false, signal: this.#abortController.signal });
    }
    this.onMediaChange();
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  get viewInSpaceButton() {
    return this.querySelector("[data-shopify-xr]");
  }
  get carousel() {
    return this.querySelector(".product-gallery__carousel");
  }
  /**
   * Create the PhotoSwipe instance if it does not already exist. This is done on demand, so until the lightbox is
   * open, nothing is created to not impact performance
   */
  get lightBox() {
    if (this.#photoSwipeInstance) {
      return this.#photoSwipeInstance;
    }
    this.#photoSwipeInstance = new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      mainClass: `color-scheme--${this.getAttribute("zoom-color-scheme-id")}`,
      // This outputs the CSS variables for the color scheme
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      // UX
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    });
    this.#photoSwipeInstance.on("uiRegister", this.registerLightboxUi);
    this.#photoSwipeInstance.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement);
    this.#photoSwipeInstance.init();
    return this.#photoSwipeInstance;
  }
  get filteredIndexes() {
    return JSON.parse(this.getAttribute("filtered-indexes")).map((index) => parseInt(index) - 1);
  }
  /**
   * Open the lightbox at the given index (by default, it opens the selected image)
   */
  openLightBox(index) {
    const images = this.carousel.cells.flatMap((cell) => Array.from(cell.querySelectorAll(":scope > img")));
    const dataSource = images.map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
    this.lightBox.loadAndOpen(index ?? imageCells.indexOf(this.carousel.selectedCell), dataSource);
  }
  /**
   * Add custom elements to PhotoSwipe gallery
   */
  registerLightboxUi() {
    this.#photoSwipeInstance.pswp.ui.registerElement({
    name: "close-button circle-button circle-button--xl",
    className: "",
    ariaLabel: window.themeVariables.strings.closeGallery,
    order: 2,
    isButton: true,
    html: `
      <svg aria-hidden="true" focusable="false" fill="none" width="14" class="icon" viewBox="0 0 16 16">
        <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
      </svg>
      `,
    onClick: () => {
        this.#photoSwipeInstance.pswp.close();
    }
    });
    if (this.#photoSwipeInstance.pswp.options.dataSource.length > 1) {
      this.#photoSwipeInstance.pswp.ui.registerElement({
      name: "previous-button circle-button circle-button--xl",
      className: "",
      ariaLabel: window.themeVariables.strings.previous,
      order: 1,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" width="12" class="icon icon--direction-aware" viewBox="0 0 36 36">
          <path fill="none" stroke="currentColor" stroke-width="2" d="M35 18H3M19.5 34.5 3 18 19.5 1.5"/>
        </svg>
        `,
      onClick: () => {
          this.#photoSwipeInstance.pswp.prev();
      }
      });
      this.#photoSwipeInstance.pswp.ui.registerElement({
      name: "next-button circle-button circle-button--xl",
      className: "",
      ariaLabel: window.themeVariables.strings.next,
      order: 3,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" width="12" class="icon icon--direction-aware" viewBox="0 0 36 36">
          <path fill="none" stroke="currentColor" stroke-width="2" d="M1 18h32M16.5 1.5 33 18 16.5 34.5"/>
        </svg>
        `,
      onClick: () => {
          this.#photoSwipeInstance.pswp.next();
      }
      });
    }
  }
  /**
   * When the section is re-rendered upon variant changes, the media might have been filtered
   */
  onSectionRerender(event) {
    const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
    if (!galleryMarkup) {
      return;
    }
    if (galleryMarkup.filteredIndex !== this.filteredIndexes) {
      this.carousel.filter(galleryMarkup.filteredIndexes);
      this.setAttribute("filtered-indexes", galleryMarkup.getAttribute("filtered-indexes"));
      if (matchesMediaQuery("md")) {
        let buttonIndex = 0;
        Array.from(this.carousel.cells).forEach((item, index) => {
          if (item.getAttribute("data-media-type") === "image") {
            item.querySelector(".product-zoom-button")?.parentElement.setAttribute("image-index", buttonIndex.toString());
            buttonIndex += 1;
          }
        });
      }
    }
  }
  /**
   * When the variant changes, we check the alt tags for each media and filter them
   */
  onVariantChange(event) {
    if (!event.detail.variant) {
      return;
    }
    if (event.detail.variant["featured_media"] && event.detail.previousVariant?.["featured_media"]?.["id"] !== event.detail.variant["featured_media"]["id"]) {
      const position = event.detail.variant["featured_media"]["position"] - 1, filteredIndexBelowPosition = this.filteredIndexes.filter((filteredIndex) => filteredIndex < position);
      if (this.carousel.isScrollable) {
        this.carousel.select(position - filteredIndexBelowPosition.length, { instant: true });
      } else {
        this.querySelector(`[data-media-id="${event.detail.variant["featured_media"]["id"]}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }
  }
  /**
   * When the media is about to change, we perform some logic
   */
  onMediaChange() {
    if (!this.#settledMedia) {
      return;
    }
    if (this.querySelector(".product-zoom-button")) {
      this.querySelector(".product-zoom-button").parentElement.toggleAttribute("hidden", this.carousel.selectedCell.getAttribute("data-media-type") !== "image");
    }
    switch (this.#settledMedia.getAttribute("data-media-type")) {
      case "external_video":
      case "video":
      case "model":
        this.#settledMedia.firstElementChild.pause();
    }
  }
  /**
   * When the media settles, we have to update various elements such as the AR button, the autoplay strategy...
   */
  onMediaSettle(event) {
    const media = event ? event.detail.cell : this.carousel.selectedCell;
    switch (media.getAttribute("data-media-type")) {
      case "image":
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
        break;
      case "external_video":
      case "video":
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
        if (this.hasAttribute("autoplay-media")) {
          media.firstElementChild.play();
        }
        break;
      case "model":
        if (matchesMediaQuery("md")) {
          media.firstElementChild.play();
        }
        this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", event.detail.cell.getAttribute("data-media-id"));
        break;
    }
    this.#settledMedia = media;
  }
  /**
   * For iOS devices only, we use the gesturechange event to easily detect a "pinch to zoom"
   */
  onGestureStart(event) {
    event.preventDefault();
    this.carousel.addEventListener("gesturechange", this.#onGestureChangedListener, { capture: false, signal: this.#abortController.signal });
  }
  onGestureChanged(event) {
    event.preventDefault();
    if (event.scale > 1.5) {
      this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: this.carousel.selectedIndex } }));
      this.removeEventListener("gesturechange", this.#onGestureChangedListener);
    }
  }
};
var OpenLightBoxButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: this.hasAttribute("image-index") ? parseInt(this.getAttribute("image-index")) : null } })));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("open-lightbox-button")) {
  window.customElements.define("open-lightbox-button", OpenLightBoxButton);
}

// js/common/product/product-list.js
import { inView as inView6, animate as animate7, stagger } from "vendor";
var ProductList = class extends ScrollCarousel {
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.querySelectorAll('product-card[reveal-on-scroll="true"]').length > 0) {
      inView6(this, this.reveal.bind(this));
    }
  }
  async reveal() {
    animate7(this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]'), {
      opacity: [0, 1],
      transform: ["translateY(20px)", "translateY(0)"]
    }, {
      duration: 0.1,
      ease: "easeInOut",
      delay: window.themeVariables.settings.staggerProducts ? stagger(0.05, { startDelay: 0.1, ease: "easeOut" }) : 0
    });
  }
};
if (!window.customElements.get("product-list")) {
  window.customElements.define("product-list", ProductList);
}

// js/common/product/product-loader.js
var loadedProducts = {};
var ProductLoader = class {
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (loadedProducts[productHandle]) {
      return loadedProducts[productHandle];
    }
    loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return loadedProducts[productHandle];
  }
};

// js/common/product/product-rerender.js
var ProductRerender = class extends HTMLElement {
  #abortController;
  constructor() {
    super(...arguments);
    this.onRerender = this.onRerender.bind(this);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    document.forms[this.getAttribute("observe-form")].addEventListener("product:rerender", this.onRerender, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  onRerender(event) {
    const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
    if (!matchingElement) {
      return;
    }
    const focusedElement = document.activeElement;
    if (!this.hasAttribute("allow-partial-rerender") || event.detail.productChange) {
      this.replaceWith(matchingElement);
    } else {
      this.querySelectorAll(`[data-block-type][data-block-allow-rerender`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type][data-block-id="${element.getAttribute("data-block-id")}"]`);
        if (matchingBlock) {
          const blockType = matchingBlock.getAttribute("data-block-type");
          if (blockType === "buy-buttons") {
            const existingQuantity = element.querySelector("quantity-selector")?.quantity;
            const buyButtons = matchingBlock.querySelector("buy-buttons");
            element.querySelector("buy-buttons").replaceWith(buyButtons);
            const newQuantitySelector = buyButtons.querySelector("quantity-selector");
            if (newQuantitySelector) {
              newQuantitySelector.quantity = existingQuantity;
            }
          } else if (blockType === "payment-terms") {
            element.querySelector('[name="id"]').value = matchingBlock.querySelector('[name="id"]').value;
            element.querySelector('[name="id"]').dispatchEvent(new Event("change", { bubbles: true }));
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    }
    if (focusedElement.id) {
      const element = document.getElementById(focusedElement.id);
      if (this.contains(element)) {
        element.focus();
      }
    }
  }
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}

// js/common/product/quick-buy-modal.js
var QuickBuyModal = class extends Modal {
  constructor() {
    super();
    this.onAfterHide = this.onAfterHide.bind(this);
    this.addEventListener("dialog:after-hide", this.onAfterHide);
  }
  get shouldAppendToBody() {
    return true;
  }
  async show() {
    const showLoadingBar = this.controls.every((control) => control.querySelector("button-content") === null);
    [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "true"));
    const promise = new Promise(async (resolve, reject) => {
      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
      }
      const responseContent = await (await cachedFetch(this.getAttribute("product-url"))).text();
      const tempDoc = new DOMParser().parseFromString(responseContent, "text/html");
      const quickBuyContent = tempDoc.getElementById("quick-buy-content");
      this.replaceChildren(quickBuyContent.content.cloneNode(true).firstElementChild);
      [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "false"));
      resolve();
      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    });
    return super.show({ conditionToFulfill: promise });
  }
  onAfterHide() {
    this.innerHTML = "";
  }
};
if (!window.customElements.get("quick-buy-modal")) {
  window.customElements.define("quick-buy-modal", QuickBuyModal);
}

// js/common/product/product-sticky-add-to-cart.js
var ProductStickyAddToCart = class extends SandwichVisibility {
  get startMarker() {
    return document.getElementById(this.getAttribute("main-product-form"));
  }
  get endMarker() {
    return document.querySelector(".shopify-section--footer");
  }
};
if (!window.customElements.get("product-sticky-add-to-cart")) {
  window.customElements.define("product-sticky-add-to-cart", ProductStickyAddToCart);
}

// js/common/product/variant-picker.js
import { Delegate as Delegate6 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var VariantPicker = class extends HTMLElement {
  static #preloadedHtml =  new Map();
  #delegate = new Delegate6(document.body);
  #intersectionObserver = new IntersectionObserver(this.onIntersection.bind(this));
  #form;
  #selectedVariant;
  constructor() {
    super(...arguments);
    this.onOptionChanged = this.onOptionChanged.bind(this);
    this.onOptionPreload = this.onOptionPreload.bind(this);
  }
  async connectedCallback() {
    this.#selectedVariant = JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}");
    this.#form = document.forms[this.getAttribute("form-id")];
    this.#delegate.on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, this.onOptionChanged);
    this.#delegate.on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, this.onOptionPreload, true);
    this.#delegate.on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, this.onOptionPreload, true);
    this.#intersectionObserver.observe(this);
    if (Shopify.designMode) {
      document.addEventListener("shopify:section:unload", () => {
        VariantPicker.#preloadedHtml.clear();
      });
    }
  }
  disconnectedCallback() {
    this.#delegate.off();
    this.#intersectionObserver.unobserve(this);
  }
  get selectedVariant() {
    return this.#selectedVariant;
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await this.renderForCombination(optionValues));
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      this.#selectedVariant = newVariant;
      this.#form.id.value = this.#selectedVariant?.id;
      this.#form.id.dispatchEvent(new Event("change", { bubbles: true }));
      if (this.updateUrl && this.#selectedVariant?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", this.#selectedVariant.id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    this.#form.dispatchEvent(new CustomEvent("product:rerender", {
      detail: {
        htmlFragment: newContent,
        productChange
      }
    }));
    if (!productChange) {
      this.#form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          formId: this.#form.id,
          variant: this.#selectedVariant,
          previousVariant
        }
      }));
    }
    Shopify?.PaymentButton?.init();
  }

  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  /**
   * Get the option values for the active combination
   */
  getActiveOptionValues() {
    return Array.from(this.#form.elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
  }
  /**
   * Get the option values for a given input
   */
  getOptionValuesFromOption(input) {
    const optionValues = [input, ...Array.from(this.#form.elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
    return optionValues;
  }
  async onOptionChanged(event) {
    if (!event.target.name.includes("option")) {
      return;
    }
    this.selectCombination({
      optionValues: this.getActiveOptionValues(),
      productChange: event.target.hasAttribute("data-product-url")
    });
  }
  /**
   * To improve the user experience, we preload a variant whenever the user hovers over a specific option
   */
  onOptionPreload(event, target) {
    this.renderForCombination(this.getOptionValuesFromOption(target.control));
  }
  /**
   * When the variant picker is intersecting the viewport, we preload the options to improve the user experience
   * so that switching variants is nearly instant
   */
  onIntersection(entries) {
    const prerenderOptions = () => {
      Array.from(this.#form.elements).filter((item) => item.matches("input[data-option-position]:not(:checked)")).forEach((input) => {
        this.renderForCombination(this.getOptionValuesFromOption(input));
      });
    };
    if (entries[0].isIntersecting) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
      } else {
        prerenderOptions();
      }
    }
  }
  async renderForCombination(optionValues) {
    const optionValuesAsString = optionValues.join(",");
    const hashKey = this.createHashKeyForHtml(optionValuesAsString);
    let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
    for (const optionValue of optionValues) {
      const inputForOptionValue = Array.from(this.#form.elements).find((item) => item.matches(`input[value="${optionValue}"]`));
      if (inputForOptionValue?.dataset.productUrl) {
        productUrl = inputForOptionValue.dataset.productUrl;
        break;
      }
    }
    if (!VariantPicker.#preloadedHtml.has(hashKey)) {
      const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
      const promise = new Promise(async (resolve) => {
        resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
      });
      VariantPicker.#preloadedHtml.set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
      if (VariantPicker.#preloadedHtml.size > 100) {
        VariantPicker.#preloadedHtml.delete(Array.from(VariantPicker.#preloadedHtml.keys())[0]);
      }
    }
    return VariantPicker.#preloadedHtml.get(hashKey).htmlPromise;
  }
  createHashKeyForHtml(optionValuesAsString) {
    return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
  }
};
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}

// js/common/media/base-media.js
import { inView as inView7 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      inView7(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    if (!this.playing && this.offsetParent !== null) {
      this.player.play();
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/media-play-button.js
var MediaPlayButton = class extends HTMLElement {
  constructor() {
    super();
    this.playMedia = this.playMedia.bind(this);
    this.addEventListener("click", this.playMedia);
  }
  playMedia() {
    document.getElementById(this.getAttribute("aria-controls"))?.play();
  }
};
if (!window.customElements.get("media-play-button")) {
  window.customElements.define("media-play-button", MediaPlayButton);
}

// js/common/media/video.js
import { inView as inView8 } from "vendor";
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  #mustRemoveControlsAfterSuspend = false;
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById("video-media-default-template").content.cloneNode(true));
    }
    if (this.getAttribute("type") === "video") {
      inView8(this, () => {
        this.querySelector("video")?.setAttribute("preload", "auto");
      }, { margin: "800px" });
    }
  }
  play({ restart = false } = {}) {
    if (restart && !this.hasAttribute("host")) {
      this.querySelector("video").currentTime = 0;
    }
    super.play();
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute("autoplay") || matchesMediaQuery("md-max");
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              "onReady": () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              "onStateChange": (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      this.querySelector("custom-cursor")?.remove();
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
        if (this.#mustRemoveControlsAfterSuspend) {
          videoElement.controls = false;
        }
      });
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            if (!this.hasAttribute("controls")) {
              this.#mustRemoveControlsAfterSuspend = true;
              target.controls = true;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// js/common/navigation/accordion-disclosure.js
import { animateSequence as animateSequence6 } from "vendor";
var _detailsElement, _summaryElement, _contentElement, _AccordionDisclosure_instances, onSummaryClick_fn, open_fn, close_fn;
var AccordionDisclosure = class extends HTMLElement {
  #detailsElement;
  #summaryElement;
  #contentElement;
  constructor() {
    super();
    this.#detailsElement = this.querySelector("details");
    this.#summaryElement = this.#detailsElement.firstElementChild;
    this.#contentElement = this.#detailsElement.lastElementChild;
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.open({ instant: event.detail.load }));
      this.addEventListener("shopify:block:deselect", this.close);
    }
    this.#summaryElement.addEventListener("click", this.onSummaryClick);
    this.classList.toggle("is-open", this.#detailsElement.open);
  }
  createOpenAnimationControls() {
    return animateSequence6([
      [this.#detailsElement, { height: [`${this.#summaryElement.clientHeight}px`, `${this.#detailsElement.clientHeight}px`] }, { duration: 0.25, ease: "easeInOut" }],
      [this.#contentElement, { opacity: [0, 1], transform: ["translateY(4px)", `translateY(0px)`] }, { duration: 0.15, at: "-0.1" }]
    ]);
  }
  createCloseAnimationControls() {
    return animateSequence6([
      [this.#contentElement, { opacity: 0 }, { duration: 0.15 }],
      [this.#detailsElement, { height: [`${this.#detailsElement.clientHeight}px`, `${this.#summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", ease: "easeInOut" }]
    ]);
  }
  onSummaryClick(event) {
    event.preventDefault();
    this.classList.toggle("is-open");
    if (this.classList.contains("is-open")) {
      this.open();
    } else {
      this.close();
    }
  }
  async open({ instant = false } = {}) {
    this.classList.add("is-open");
    this.#detailsElement.open = true;
    this.#detailsElement.style.overflow = "hidden";
    const animationControls = this.createOpenAnimationControls();
    if (instant) {
      animationControls.complete();
    }
    await animationControls;
    this.#detailsElement.style.height = null;
    this.#detailsElement.style.overflow = null;
  }
  async close() {
    this.classList.remove("is-open");
    const animationControls = this.createCloseAnimationControls();
    await animationControls;
    if (!this.classList.contains("is-open")) {
      this.#detailsElement.style.height = null;
      this.#detailsElement.open = false;
    }
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure);
}

// js/common/navigation/tabs.js
import { Delegate as Delegate7, animate as animate8 } from "vendor";
var Tabs = class extends HTMLElement {
  #componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);
  #buttons = [];
  #panels = [];
  #delegate = new Delegate7(this);
  constructor() {
    super();
    this.onButtonClicked = this.onButtonClicked.bind(this);
    this.onSlotChange = this.onSlotChange.bind(this);
    this.handleKeyboard = this.handleKeyboard.bind(this);
    if (!this.shadowRoot) {
      const template2 = this.querySelector("template") || this.defaultTemplate;
      this.attachShadow({ mode: "open" }).appendChild(template2.content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.#buttons.indexOf(event.target));
    }
    this.#delegate.on("click", 'button[role="tab"]', this.onButtonClicked);
    this.shadowRoot.addEventListener("slotchange", this.onSlotChange);
    this.addEventListener("keydown", this.handleKeyboard);
  }
  static get observedAttributes() {
    return ["selected-index"];
  }
  connectedCallback() {
    this.setupComponent();
    this.selectedIndex = this.selectedIndex;
  }
  /**
   * --------------------------------------------------------------------------
   * GETTERS AND SETTERS
   * --------------------------------------------------------------------------
   */
  get defaultTemplate() {
    const template2 = document.createElement("template");
    template2.innerHTML = `
      <slot role="tablist" part="tab-list" name="tab"></slot>
      <slot part="tab-panels" name="tabpanel"></slot>
    `;
    return template2;
  }
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), this.#buttons.length - 1).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
    this.#buttons.forEach((button, i) => {
      if (i === index) {
        button.removeAttribute("tabindex");
        this.scrollTo({ left: button.offsetLeft - this.clientWidth / 2 + button.clientWidth / 2, behavior: "smooth" });
      } else {
        button.tabIndex = -1;
      }
    });
  }
  /**
   * --------------------------------------------------------------------------
   * METHODS
   * --------------------------------------------------------------------------
   */
  attributeChangedCallback(name, oldValue, newValue) {
    this.#buttons.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      this.transition(this.#panels[parseInt(oldValue)], this.#panels[parseInt(newValue)]);
    }
  }
  /**
   * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
   */
  async transition(fromPanel, toPanel) {
    await animate8(fromPanel, { opacity: [1, 0], transform: ["translateY(0)", "translateY(10px)"] }, { duration: this.animationDuration });
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await animate8(toPanel, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: this.animationDuration });
  }
  setupComponent() {
    this.#buttons = Array.from(this.shadowRoot.querySelector('slot[name="tab"]').assignedNodes(), (item) => item.matches("button") && item || item.querySelector("button")).filter((button) => button !== null);
    this.#panels = [];
    if (this.#buttons.length > 0) {
      this.#buttons.forEach((button, index) => {
        button.setAttribute("role", "tab");
        button.id = `tab-${this.#componentID}-${index}`;
        let panel;
        if (button.hasAttribute("aria-controls")) {
          panel = document.getElementById(button.getAttribute("aria-controls"));
        } else {
          panel = this.shadowRoot.querySelector('slot[name="tabpanel"]').assignedNodes()[index];
          panel.id = `tab-panel-${this.#componentID}-${index}`;
          button.setAttribute("aria-controls", `tab-panel-${this.#componentID}-${index}`);
        }
        if (!panel) {
        } else {
          panel.setAttribute("role", "tabpanel");
          panel.setAttribute("aria-labelledby", `tab-${this.#componentID}-${index}`);
          panel.hidden = index !== this.selectedIndex;
          if (!panel.matches(":scope:has(a[href], button:not([disabled]), details, iframe, object, input:not([disabled]), select:not([disabled]), textarea:not([disabled]))")) {
            panel.tabIndex = 0;
          }
          this.#panels[index] = panel;
        }
      });
    }
    if (!this.#buttons.find((button) => button.getAttribute("aria-selected") === "true")) {
      this.selectedIndex = 0;
    }
    this.style.setProperty("--item-count", this.#buttons.length.toString());
  }
  onButtonClicked(event, button) {
    this.selectedIndex = this.#buttons.indexOf(button);
  }
  onSlotChange(event) {
    this.setupComponent();
  }
  /**
   * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
   * left and right arrow should switch the tab
   */
  handleKeyboard(event) {
    const index = this.#buttons.indexOf(document.activeElement);
    if (index === -1) {
      return;
    }
    if (event.key === "ArrowLeft") {
      this.selectedIndex = (this.selectedIndex - 1 + this.#buttons.length) % this.#buttons.length;
      this.#buttons[this.selectedIndex].focus();
    } else if (event.key === "ArrowRight") {
      this.selectedIndex = (this.selectedIndex + 1 + this.#buttons.length) % this.#buttons.length;
      this.#buttons[this.selectedIndex].focus();
    }
  }
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}

// js/sections/ingredient-glossary.js
var IngredientGlossary = class extends HTMLElement {
  #buttons = [];
  #cards = [];
  #countNode = null;
  #countTemplate = "";
  #countTemplateOne = "";
  #countTemplateZero = "";
  connectedCallback() {
    this.#buttons = Array.from(this.querySelectorAll("[data-ingredient-filter]"));
    this.#cards = Array.from(this.querySelectorAll("[data-ingredient-card]"));
    this.#countNode = this.querySelector("[data-ingredient-count]");
    this.#countTemplate = this.getAttribute("data-count-template") || "";
    this.#countTemplateOne = this.getAttribute("data-count-template-one") || "";
    this.#countTemplateZero = this.getAttribute("data-count-template-zero") || "";
    this.#buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        if (button.disabled) {
          return;
        }
        this.applyFilter(button.getAttribute("data-ingredient-filter"));
        this.syncUrl(button.getAttribute("data-ingredient-filter"));
      });
    });
    const initialFilter = this.getInitialFilter();
    this.applyFilter(initialFilter);
    if (initialFilter !== "all") {
      this.syncUrl(initialFilter);
    }
  }
  getInitialFilter() {
    const letterParam = new URLSearchParams(window.location.search).get("letter");
    if (!letterParam) {
      return "all";
    }
    const normalized = letterParam.trim();
    if (normalized.toLowerCase() === "all") {
      return "all";
    }
    if (normalized === "#") {
      return "#";
    }
    const upper = normalized.toUpperCase();
    const matchingButton = this.#buttons.find((button) => button.getAttribute("data-ingredient-filter") === upper && !button.disabled);
    return matchingButton ? upper : "all";
  }
  applyFilter(filter) {
    const activeFilter = filter || "all";
    let visibleCount = 0;
    this.#buttons.forEach((button) => {
      const buttonFilter = button.getAttribute("data-ingredient-filter");
      const isActive = buttonFilter === activeFilter;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
      if (isActive) {
        const panelId = button.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          panel.setAttribute("aria-labelledby", button.id);
        }
      }
    });
    this.#cards.forEach((card) => {
      const cardLetter = card.getAttribute("data-letter");
      const show = activeFilter === "all" || cardLetter === activeFilter;
      card.hidden = !show;
      if (show) {
        visibleCount += 1;
      }
    });
    this.updateCount(visibleCount, activeFilter);
  }
  updateCount(visibleCount, filter) {
    if (!this.#countNode) {
      return;
    }
    if (filter !== "all" && visibleCount === 0) {
      this.#countNode.textContent = this.#countTemplateZero;
      return;
    }
    if (visibleCount === 1) {
      this.#countNode.textContent = this.#countTemplateOne;
      return;
    }
    this.#countNode.textContent = this.#countTemplate.replace("{{ count }}", visibleCount.toString());
  }
  syncUrl(filter) {
    const url = new URL(window.location.href);
    if (!filter || filter === "all") {
      url.searchParams.delete("letter");
    } else {
      url.searchParams.set("letter", filter);
    }
    history.replaceState({}, "", url);
  }
};
if (!window.customElements.get("ingredient-glossary")) {
  window.customElements.define("ingredient-glossary", IngredientGlossary);
}

// js/common/search/predictive-search.js
import { Delegate as Delegate8 } from "vendor";
var PredictiveSearch = class extends HTMLElement {
  #delegate = new Delegate8(this);
  #listenersAbortController;
  #fetchAbortController;
  #searchForm;
  #queryInput;
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="placeholder"></slot>`));
    this.onFormSubmitted = this.onFormSubmitted.bind(this);
    this.onSearchCleared = this.onSearchCleared.bind(this);
    this.onInputChanged = this.onInputChanged.bind(this);
  }
  connectedCallback() {
    this.#listenersAbortController = new AbortController();
    this.#searchForm = document.querySelector(`[aria-owns="${this.id}"]`);
    this.#queryInput = this.#searchForm.elements["q"];
    this.#searchForm.addEventListener("submit", this.onFormSubmitted, { signal: this.#listenersAbortController.signal });
    this.#searchForm.addEventListener("reset", this.onSearchCleared, { signal: this.#listenersAbortController.signal });
    this.#queryInput.addEventListener("input", debounce(this.onInputChanged, this.autoCompleteDelay, { signal: this.#listenersAbortController.signal }));
  }
  disconnectedCallback() {
    this.#listenersAbortController?.abort();
  }
  /**
   * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
   * refresh too often, so we recommend to keep the default one
   */
  get autoCompleteDelay() {
    return 280;
  }
  /**
   * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
   * search is simply disabled and only the standard search is used
   */
  supportsPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
  }

  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  /**
   * Check if the input is not empty, and if so, trigger the predictive search
   */
  onInputChanged() {
    if (this.#queryInput.value === "") {
      return this.onSearchCleared();
    }
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();
    return this.doPredictiveSearch();
  }
  /**
   * Prevent the form submission if the query is empty
   */
  onFormSubmitted(event) {
    if (this.#queryInput.value === "") {
      return event.preventDefault();
    }
  }
  async doPredictiveSearch() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    try {
      const url = `${window.Shopify.routes.root}search${this.supportsPredictiveApi() ? "/suggest" : ""}`, queryParams = `q=${encodeURIComponent(this.#queryInput.value)}&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each`, tempDoc = new DOMParser().parseFromString(await (await cachedFetch(`${url}?${queryParams}`, { signal: this.#fetchAbortController.signal })).text(), "text/html");
      this.shadowRoot.innerHTML = `<slot name="results"></slot>`;
      this.querySelector('[slot="results"]').replaceChildren(...document.importNode(tempDoc.querySelector(".shopify-section"), true).children);
    } catch (e) {
      if (e.name !== "AbortError") {
        throw e;
      }
    }
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  }
  /**
   * If any search is pending, we abort them, and transition to the idle slot
   */
  onSearchCleared() {
    this.#fetchAbortController?.abort();
    this.#queryInput.focus();
    this.shadowRoot.innerHTML = `<slot name="placeholder"></slot>`;
  }
};
if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

// js/common/typography/button.js
import { animate as animate9, animateSequence as animateSequence7 } from "vendor";
var ButtonContent = class extends HTMLElement {
  #isLoading = false;
  #mutationObserver;
  #abortController;
  constructor() {
    super();
    this.startHoverEffect = this.startHoverEffect.bind(this);
    this.finishHoverEffect = this.finishHoverEffect.bind(this);
    this.onObserveAttributes = this.onObserveAttributes.bind(this);
    this.onLoadingChanged = this.onLoadingChanged.bind(this);
    this.#mutationObserver = new MutationObserver(this.onObserveAttributes);
  }
  connectedCallback() {
    this.style.transform = "";
    this.#mutationObserver.observe(this.parentElement, { attributes: true, attributeFilter: ["aria-busy"], attributeOldValue: true });
    this.#abortController = new AbortController();
    if (this.hasAttribute("rotated-text") && matchesMediaQuery("supports-hover") && matchesMediaQuery("motion-safe")) {
      this.parentElement.addEventListener("mouseenter", this.startHoverEffect, { signal: this.#abortController.signal });
      this.parentElement.addEventListener("mouseleave", this.finishHoverEffect, { signal: this.#abortController.signal });
    }
  }
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#abortController.abort();
  }
  startHoverEffect() {
    if (this.#isLoading) {
      return;
    }
    const contentWidth = getComputedStyle(this, "::before").width;
    this.getAnimations().forEach((animation) => animation.cancel());
    const SPEED_PX_PER_SECOND = 100;
    const distance = this.clientWidth + parseFloat(contentWidth);
    const animationDuration = distance / SPEED_PX_PER_SECOND;
    return animate9(
      this,
      { transform: [null, `translateX(calc(var(--transform-logical-flip) * (50% + ${contentWidth} / 2)))`] },
      { repeat: Infinity, ease: "linear", duration: animationDuration }
    );
  }
  finishHoverEffect() {
    if (!this?.checkVisibility()) {
      return;
    }
    return animate9(
      this,
      { transform: [null, "translateX(0px)"] },
      { ease: [0.33, 1, 0.68, 1], duration: 0.2 }
    );
  }
  /**
   * Detect whenever the button (the parent) has the attribute "aria-busy" being added, so that we display the transient content or not
   */
  onObserveAttributes(mutationList) {
    mutationList.forEach((mutation) => {
      switch (mutation.type) {
        case "attributes":
          switch (mutation.attributeName) {
            case "aria-busy":
              if (mutation.oldValue === mutation.target.getAttribute("aria-busy")) {
                break;
              }
              this.onLoadingChanged(mutation.target.getAttribute("aria-busy") === "true");
              break;
          }
          break;
      }
    });
  }
  async onLoadingChanged(loading) {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" });
      let transientContent2 = "";
      if (this.closest("copy-button")) {
        transientContent2 = `
          <span class="transient" style="display: flex; align-items: center; gap: 8px">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 12 12">
              <path stroke="currentColor" stroke-width="1.5" d="M1.5 4.643 5 8.5l5.5-6"/>
            </svg>

            <span>Copied</span>
          </span>
        `;
      } else {
        transientContent2 = `
          <svg class="transient" width="20" viewBox="0 0 120 30" fill="currentColor" style="opacity: 0">
            <circle cx="15" cy="15" r="15">
              <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite"/>
            </circle>
            <circle cx="60" cy="15" r="9" fill-opacity="0.3">
              <animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5" calcMode="linear" repeatCount="indefinite"/>
            </circle>
            <circle cx="105" cy="15" r="15">
              <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }
      this.shadowRoot.appendChild(document.createRange().createContextualFragment(`
        <style>
          :host {
            display: grid !important;
            place-items: center !important;
          }

          .transient {
            opacity: 0;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        </style>

        ${transientContent2}

        <div part="content">
          <slot></slot>
        </div>
      `));
    }
    this.#isLoading = loading;
    const transientContent = this.shadowRoot.querySelector(".transient");
    const content = this.shadowRoot.querySelector('[part="content"]');
    if (!this?.checkVisibility()) {
      return;
    }
    if (loading) {
      if (this.hasAttribute("rotated-text")) {
        await this.finishHoverEffect();
      }
      if (this.#isLoading) {
        animateSequence7([
          [content, { transform: ["translateY(0)", "translateY(-10px)"], opacity: [1, 0] }, { duration: 0.18 }],
          [transientContent, { transform: ["translate(-50%, calc(-50% + 10px))", "translate(-50%, -50%)"], opacity: [0, 1] }, { duration: 0.18 }]
        ]);
      }
    } else {
      animateSequence7([
        [transientContent, { transform: ["translate(-50%, -50%)", "translateY(-50%, calc(-50% + 10px))"], opacity: [1, 0] }, { duration: 0.18 }],
        [content, { transform: ["translateY(10px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.18 }]
      ]);
    }
  }
};
if (!window.customElements.get("button-content")) {
  window.customElements.define("button-content", ButtonContent);
}

// js/sections/announcement-bar.js
import { animate as animate11 } from "vendor";
var AnnouncementBar = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, ease: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, ease: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("announcement-bar")) {
  window.customElements.define("announcement-bar", AnnouncementBar);
}

// js/sections/blog-post-list.js
import { inView as inView11, animate as animate13, stagger as stagger2 } from "vendor";
var BlogPostList = class extends ScrollCarousel {
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.querySelectorAll(':scope > [reveal-on-scroll="true"]').length > 0) {
      inView11(this, this.reveal.bind(this));
    }
  }
  reveal() {
    animate13(this.querySelectorAll(':scope > [reveal-on-scroll="true"]'), {
      opacity: [0, 1],
      transform: ["translateY(20px)", "translateY(0)"]
    }, {
      duration: 0.1,
      ease: "easeInOut",
      delay: stagger2(0.05, { startDelay: 0.1, ease: "easeOut" })
    });
  }
};
if (!window.customElements.get("blog-post-list")) {
  window.customElements.define("blog-post-list", BlogPostList);
}

// js/sections/cart-drawer.js
var CartDrawer = class extends Drawer {
  #sectionId;
  constructor() {
    super(...arguments);
    this.onBundleSection = this.onBundleSection.bind(this);
    this.onCartChange = this.onCartChange.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.refreshCart = this.refreshCart.bind(this);
  }
  // We have to save the section ID, as the drawer may be replaced in the DOM
  connectedCallback() {
    super.connectedCallback();
    this.#sectionId ??= extractSectionId(this);
    document.addEventListener("cart:prepare-bundled-sections", this.onBundleSection, { signal: this.abortController.signal });
    document.addEventListener("cart:change", this.onCartChange, { signal: this.abortController.signal });
    document.addEventListener("cart:refresh", this.refreshCart, { signal: this.abortController.signal });
    window.addEventListener("pageshow", this.onPageShow, { signal: this.abortController.signal });
  }
  get initialFocus() {
    return false;
  }
  /**
   * This method is called when the cart is changing, and allow custom sections to order a "re-render"
   */
  onBundleSection(event) {
    event.detail.sections.add(this.#sectionId);
  }
  /**
   * When the cart changes, we need to re-render the cart drawer
   */
  onCartChange(event) {
    const html = new DOMParser().parseFromString(event.detail.cart["sections"][this.#sectionId], "text/html"), itemCount = event.detail.cart["item_count"], newCartDrawer = document.createRange().createContextualFragment(html.getElementById(`shopify-section-${this.#sectionId}`).querySelector("cart-drawer").innerHTML);
    if (itemCount === 0) {
      this.replaceChildren(...newCartDrawer.children);
    } else {
      this.replaceChildren(...newCartDrawer.children);
    }
    this.replaceContent(event.detail.cart["sections"][this.#sectionId]);
    if (window.themeVariables.settings.cartType === "drawer" && !this.open && event.detail.baseEvent === "variant:add") {
      this.show();
    }
  }
  async onPageShow(event) {
    if (!event.persisted) {
      return;
    }
    this.refreshCart();
  }
  async refreshCart() {
    this.replaceContent(await (await fetch(`${Shopify.routes.root}?section_id=${this.#sectionId}`)).text());
  }
  async replaceContent(html) {
    const domElement = new DOMParser().parseFromString(html, "text/html"), newCartDrawer = document.createRange().createContextualFragment(domElement.getElementById(`shopify-section-${this.#sectionId}`).querySelector("cart-drawer").innerHTML), itemCount = (await fetchCart)["item_count"];
    if (itemCount === 0) {
      this.replaceChildren(...newCartDrawer.children);
    } else {
      this.replaceChildren(...newCartDrawer.children);
    }
    this.dispatchEvent(new CustomEvent("cart-drawer:refreshed", { bubbles: true }));
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}

// js/sections/featured-collections.js
var FeaturedCollectionsTabs = class extends Tabs {
  async transition(fromPanel, toPanel) {
    super.transition(fromPanel, toPanel);
    if (window.themeVariables.settings.staggerProducts) {
      toPanel.querySelector("product-list").reveal();
    }
    if (toPanel.hasAttribute("data-url")) {
      this.nextElementSibling?.setAttribute("href", toPanel.getAttribute("data-url"));
    }
  }
};
if (!window.customElements.get("featured-collections-tabs")) {
  window.customElements.define("featured-collections-tabs", FeaturedCollectionsTabs);
}

// js/sections/header.js
import { animate as animate15, animateSequence as animateSequence8, stagger as stagger3, frame, Delegate as Delegate9 } from "vendor";
var Header = class extends HTMLElement {
  #headerTrackerIntersectionObserver = new IntersectionObserver(this.onHeaderTrackerIntersection.bind(this));
  #abortController;
  #scrollYTrackingPosition = 0;
  #isVisible = true;
  #onHeaderCartChange = (event) => {
    const count = event.detail?.["cart"]?.["item_count"];
    if (typeof count === "number") {
      this.#syncHeaderCartCount(count);
    }
  };
  #onHeaderCartRefresh = async () => {
    if (!this.querySelector("[data-header-cart-count]")) {
      return;
    }
    const cart = await fetchCart;
    if (typeof cart?.item_count === "number") {
      this.#syncHeaderCartCount(cart.item_count);
    }
  };
  constructor() {
    super(...arguments);
    this.detectMousePosition = this.detectMousePosition.bind(this);
    this.detectScrollDirection = this.detectScrollDirection.bind(this);
  }
  #syncHeaderCartCount(count) {
    const span = this.querySelector("[data-header-cart-count]");
    if (!span) {
      return;
    }
    span.textContent = String(count);
    const anchor = span.closest("a[data-cart-aria-label-template]");
    const tpl = anchor?.getAttribute("data-cart-aria-label-template");
    if (tpl) {
      anchor.setAttribute("aria-label", tpl.replace(/__COUNT__/g, String(count)));
    }
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    this.#headerTrackerIntersectionObserver.observe(document.getElementById("header-scroll-tracker"));
    if (this.querySelector("[data-header-cart-count]")) {
      document.addEventListener("cart:change", this.#onHeaderCartChange, { signal: this.#abortController.signal });
      document.addEventListener("cart:refresh", this.#onHeaderCartRefresh, { signal: this.#abortController.signal });
      window.addEventListener("pageshow", this.#onHeaderCartRefresh, { signal: this.#abortController.signal });
    }
    if (this.hasAttribute("hide-on-scroll")) {
      window.addEventListener("scroll", throttle(this.detectScrollDirection), { signal: this.#abortController.signal, passive: true });
      window.addEventListener("pointermove", this.detectMousePosition, { signal: this.#abortController.signal });
    }
    document.addEventListener("header:allow-transparent-header", () => this.classList.remove("is-solid"), { signal: this.#abortController.signal });
    document.addEventListener("header:disable-transparent-header", () => this.classList.add("is-solid"), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  onHeaderTrackerIntersection(entries) {
    this.classList.toggle("is-solid", !entries[0].isIntersecting);
  }
  detectMousePosition(event) {
    if (event.clientY < 100 && window.matchMedia("screen and (pointer: fine)").matches) {
      this.setVisibility(true);
      this.#scrollYTrackingPosition = 0;
    }
  }
  detectScrollDirection() {
    let isVisible;
    let absScrollY = Math.abs(window.scrollY);
    if (absScrollY > this.#scrollYTrackingPosition && absScrollY - this.#scrollYTrackingPosition > 100) {
      isVisible = false;
      this.#scrollYTrackingPosition = absScrollY;
    } else if (absScrollY < this.#scrollYTrackingPosition) {
      this.#scrollYTrackingPosition = absScrollY;
      isVisible = true;
    }
    if (isVisible !== void 0) {
      this.setVisibility(isVisible);
    }
  }
  setVisibility(isVisible) {
    if (isVisible !== this.#isVisible) {
      if (!isVisible && this.querySelectorAll("[open]").length > 0) {
        return;
      }
      this.#isVisible = isVisible;
      document.documentElement.style.setProperty("--header-is-visible", isVisible ? "1" : "0");
      this.classList.toggle("is-hidden", !isVisible);
    }
  }
};
var HeaderDropdownMenu = class extends DialogElement {
  get trapStack() {
    return [];
  }
  createEnterAnimationControls() {
    return animateSequence8([[this, { opacity: [0, 1] }, { duration: 0.25, ease: "easeOut" }]]);
  }
  createLeaveAnimationControls() {
    return animateSequence8([
      [this, { opacity: [1, 0] }, { duration: 0.2 }]
    ]);
  }
};
var HeaderMegaMenu = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    if (!this.classList.contains("shop-mega-menu")) {
      return;
    }
    const root = this.querySelector("[data-shop-mega-menu]");
    if (!root) {
      return;
    }
    const tabs = Array.from(root.querySelectorAll("[data-shop-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-shop-panel]"));
    if (tabs.length === 0 || panels.length === 0) {
      return;
    }
    const signal = this.abortController.signal;
    const selectTab = (index) => {
      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
        const panel = panels[i];
        if (!panel) {
          return;
        }
        panel.classList.toggle("is-active", selected);
        panel.setAttribute("aria-hidden", selected ? "false" : "true");
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener("pointerenter", () => selectTab(index), { signal });
      tab.addEventListener("focusin", () => selectTab(index), { signal });
      tab.addEventListener("click", () => selectTab(index), { signal });
    });
    this.addEventListener("dialog:before-show", () => selectTab(0), { signal });
  }
  createEnterAnimationControls() {
    return animateSequence8([[this, { opacity: [0, 1] }, { duration: 0.25, ease: "easeOut" }]]);
  }
  createLeaveAnimationControls() {
    return animateSequence8([
      [this, { opacity: [1, 0] }, { duration: 0.2 }]
    ]);
  }
};
var HeaderMenuDrawer = class extends DialogElement {
  #delegate = new Delegate9(this);
  #isTransitioningPanel = false;
  constructor() {
    super();
    this.onBeforeShow = this.onBeforeShow.bind(this);
    this.onAfterHide = this.onAfterHide.bind(this);
    this.calculateOffsets = this.calculateOffsets.bind(this);
    this.addEventListener("dialog:before-show", this.onBeforeShow);
    this.addEventListener("dialog:after-hide", this.onAfterHide);
    this.#delegate.on("click", "[data-panel-toggle]", (event, target) => this.onSwitchToPanel(target.getAttribute("aria-controls")));
    if (window.onscrollend !== void 0) {
      window.addEventListener("scrollend", this.calculateOffsets);
    } else {
      window.addEventListener("scroll", this.calculateOffsets);
    }
  }
  get shouldLock() {
    return true;
  }
  #resetDrawerPanelStyles() {
    this.querySelectorAll("header-menu-drawer-panel").forEach((panel) => panel.resetStyles());
    const content = this.getShadowPartByName("content");
    if (content) {
      content.style.removeProperty("opacity");
      content.style.removeProperty("transform");
    }
  }
  createEnterAnimationControls() {
    this.#resetDrawerPanelStyles();
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(calc(var(--transform-logical-flip) * -100%))", "translateX(0)"] }, { duration: 0.35, at: "<", ease: [0.2, 0.4, 0.2, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [0, 1] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence8(timelineSteps);
  }
  createLeaveAnimationControls() {
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.25 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * -100%))"] }, { duration: 0.25, ease: [0.645, 0.045, 0.355, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [1, 0] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence8(timelineSteps);
  }
  /**
   * Before the drawer is open, we calculate the offsets and show the main panel
   */
  onBeforeShow() {
    this.calculateOffsets();
    this.#resetDrawerPanelStyles();
    this.querySelector(`#menu-drawer-panel-main`)?.show({ initial: true });
  }
  /**
   * After the drawer is fully closed, we hide all existing panels
   */
  onAfterHide() {
    this.querySelectorAll("header-menu-drawer-panel").forEach((panel) => panel.hide({ instant: true }));
    this.querySelectorAll("accordion-disclosure.is-open").forEach((accordion) => accordion.close());
    this.#resetDrawerPanelStyles();
  }
  async onSwitchToPanel(panelId) {
    if (this.#isTransitioningPanel) {
      return;
    }
    const fromPanel = this.querySelector(`header-menu-drawer-panel.is-visible`);
    const toPanel = this.querySelector(`header-menu-drawer-panel#${panelId}`);
    this.#isTransitioningPanel = true;
    const hidePromise = fromPanel.hide();
    const showPromise = toPanel.show({ initial: false });
    Promise.all([hidePromise, showPromise]).then(() => {
      this.#isTransitioningPanel = false;
    });
  }
  calculateOffsets() {
    if (!this.open) {
      return;
    }
    const boundingRect = this.getShadowPartByName("content").getBoundingClientRect();
    this.style.setProperty("--menu-offset-top", `${boundingRect.top}px`);
  }
};
var HeaderMenuDrawerPanel = class extends HTMLElement {
  resetStyles() {
    this.style.cssText = "";
    this.querySelectorAll(".menu-drawer__panel-body > *, .menu-drawer__panel-footer").forEach((element) => {
      element.style.cssText = "";
    });
  }
  async show({ initial = true } = {}) {
    this.resetStyles();
    this.classList.add("is-visible");
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      const rtlFlip = document.dir === "rtl" ? -1 : 1;
      if (initial) {
        return animateSequence8([
          [this.querySelectorAll(".menu-drawer__panel-body > *"), { opacity: [0, 1], transform: ["translateY(1em)", "translateY(0)"] }, { duration: 0.2, delay: stagger3(0.1, { startDelay: 0.4 }) }],
          [this.querySelector(".menu-drawer__panel-footer"), { opacity: [0, 1], transform: ["translateY(1em)", "translateY(0)"] }, { duration: 0.2, at: "-0.1", ease: "easeInOut" }]
        ]);
      } else {
        if (this.hasAttribute("data-main-panel")) {
          return animateSequence8([
            [this, { transform: [`translateX(${-30 * rtlFlip}%)`, "translateX(0)"] }, { duration: 0.25, ease: "easeInOut" }]
          ]);
        } else {
          return animateSequence8([
            [this, { transform: [`translateX(${rtlFlip * 100}%)`, "translateX(0)"] }, { duration: 0.4, ease: [0.645, 0.045, 0.355, 1] }]
          ]);
        }
      }
    } else {
      return animateSequence8([
        [this, { opacity: [0, 1] }, { duration: 0.2 }]
      ]);
    }
  }
  async hide({ instant = false } = {}) {
    let promise = Promise.resolve();
    if (!instant) {
      if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
        const rtlFlip = document.dir === "rtl" ? -1 : 1;
        if (this.hasAttribute("data-main-panel")) {
          promise = animateSequence8([
            [this, { transform: ["translateX(0)", `translateX(${-30 * rtlFlip}%)`] }, { duration: 0.4, ease: [0.645, 0.045, 0.355, 1] }]
          ]);
        } else {
          promise = animateSequence8([
            [this, { transform: ["translateX(0%)", `translateX(${100 * rtlFlip}%)`] }, { duration: 0.25, ease: "easeInOut" }]
          ]);
        }
      } else {
        promise = animateSequence8([
          [this, { opacity: [1, 0] }, { duration: 0.2 }]
        ]);
      }
    }
    return promise.then(() => {
      frame.render(() => {
        this.resetStyles();
        this.classList.remove("is-visible");
      });
    });
  }
};
if (!window.customElements.get("x-header")) {
  window.customElements.define("x-header", Header);
}
if (!window.customElements.get("header-dropdown-menu")) {
  window.customElements.define("header-dropdown-menu", HeaderDropdownMenu);
}
if (!window.customElements.get("header-mega-menu")) {
  window.customElements.define("header-mega-menu", HeaderMegaMenu);
}
if (!window.customElements.get("header-menu-drawer")) {
  window.customElements.define("header-menu-drawer", HeaderMenuDrawer);
}
if (!window.customElements.get("header-menu-drawer-panel")) {
  window.customElements.define("header-menu-drawer-panel", HeaderMenuDrawerPanel);
}

// js/sections/main-search.js
var SearchResultPanel = class extends HTMLElement {
  async connectedCallback() {
    if (!this.hasAttribute("load-from-url")) {
      return;
    }
    const textResponse = await (await fetch(encodeURI(`${this.getAttribute("load-from-url")}&section_id=${extractSectionId(this)}`))).text();
    const temporaryContent = new DOMParser().parseFromString(textResponse, "text/html");
    const searchResultsPanel = temporaryContent.querySelector(`#${this.getAttribute("id")}`);
    if (searchResultsPanel) {
      this.replaceChildren(...searchResultsPanel.children);
    } else {
      document.querySelector(`[aria-controls="${this.id}"]`).remove();
      this.remove();
    }
  }
};
if (!window.customElements.get("search-result-panel")) {
  window.customElements.define("search-result-panel", SearchResultPanel);
}

// js/sections/product-recommendations.js
var ProductRecommendations = class extends HTMLElement {
  #isLoaded = false;
  constructor() {
    super(...arguments);
    this.loadRecommendations = this.loadRecommendations.bind(this);
  }
  connectedCallback() {
    this.loadRecommendations();
  }
  async loadRecommendations() {
    if (this.#isLoaded) {
      return;
    }
    this.#isLoaded = true;
    const intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${extractSectionId(this)}&intent=${intent}`, response = await fetch(url, { priority: intent === "related" ? "low" : "auto" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
    if (productRecommendationsElement.childElementCount > 0) {
      this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
      this.hidden = false;
    } else {
      this.remove();
    }
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}

// js/sections/recently-viewed-products.js
var RecentlyViewedProducts = class extends HTMLElement {
  #isLoaded = false;
  constructor() {
    super(...arguments);
    this.loadProducts = this.loadProducts.bind(this);
  }
  connectedCallback() {
    this.loadProducts();
  }
  get searchQueryString() {
    const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));
    if (this.hasAttribute("exclude-id")) {
      items.delete(parseInt(this.getAttribute("exclude-id")));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
  }
  async loadProducts() {
    if (this.#isLoaded) {
      return;
    }
    this.#isLoaded = true;
    const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${this.searchQueryString}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
    if (recentlyViewedProductsElement.childElementCount > 0) {
      this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
    } else {
      section.remove();
    }
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// js/sections/slideshow.js
import { animate as animate22, animateSequence as animateSequence13 } from "vendor";
var Slideshow = class extends EffectCarousel {
  #navigationButtonAnimationControls;
  constructor() {
    super();
    this.onSlideChange = this.onSlideChange.bind(this);
    this.onPlayerStart = this.onPlayerStart.bind(this);
    this.onPlayerPause = this.onPlayerPause.bind(this);
    this.onPlayerStop = this.onPlayerStop.bind(this);
    this.onPlayerResume = this.onPlayerResume.bind(this);
    this.addEventListener("carousel:change", this.onSlideChange);
    if (this.player) {
      this.player.addEventListener("player:start", this.onPlayerStart);
      this.player.addEventListener("player:pause", this.onPlayerPause);
      this.player.addEventListener("player:stop", this.onPlayerStop);
      this.player.addEventListener("player:resume", this.onPlayerResume);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    this.onSlideChange({ detail: { cell: this.selectedCell } });
  }
  get blockChangeWhenTransitioning() {
    return this.effect !== "fade";
  }
  get effect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return "fade";
    }
    return this.getAttribute("transition");
  }
  /**
   * If the slide is a video, we return the video duration instead of the default slide duration
   */
  async getPlayerDurationForSlide(slide) {
    if (slide.getAttribute("data-slide-type") === "video") {
      const video = Array.from(slide.querySelectorAll("video")).filter((video2) => video2.offsetParent !== null).pop();
      if (isNaN(video.duration)) {
        await new Promise((resolve) => {
          video.onloadedmetadata = () => resolve();
        });
      }
      return video.duration;
    }
    return super.getPlayerDurationForSlide(slide);
  }
  /**
   * Reference for animations: https://tympanus.net/Development/SlideshowAnimations/index.html
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    let flip;
    let animationControls;
    switch (this.effect) {
      case "fade":
        animationControls = super.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
        break;
      case "vertical_reveal":
        flip = direction === "next" ? document.dir === "rtl" ? -1 : 1 : document.dir === "rtl" ? 1 : -1;
        animationControls = animateSequence13([
          [fromSlide, { transform: ["translateY(0)", `translateY(${flip * -100}%)`] }, { duration: 0.3, ease: [0.55, 0.085, 0.68, 0.53], at: 0 }],
          [fromSlide.firstElementChild, { transform: ["translateY(0)", `translateY(${flip * 75}%)`] }, { duration: 0.3, ease: [0.55, 0.085, 0.68, 0.53], at: 0 }],
          { name: "middle", at: "+0.1" },
          [toSlide, { transform: [`translateY(${flip * 100}%)`, "translateY(0)"], opacity: [1, 1] }, { duration: 0.7, ease: [0.19, 1, 0.22, 1], at: "middle" }],
          [toSlide.firstElementChild, { transform: [`translateY(${flip * -75}%)`, "translateY(0)"] }, { duration: 0.7, ease: [0.19, 1, 0.22, 1], at: "middle" }]
        ]);
        break;
      case "curtain":
        flip = direction === "next" ? document.dir === "rtl" ? -1 : 1 : document.dir === "rtl" ? 1 : -1;
        animationControls = animateSequence13([
          [fromSlide, { transform: ["translateX(0)", `translateX(${flip * -100}%)`] }, { ease: [0.77, 0, 0.175, 1], at: 0 }],
          [fromSlide.firstElementChild, { transform: ["translateX(0)", `translateX(${flip * 30}%)`] }, { ease: [0.77, 0, 0.175, 1], at: 0 }],
          [toSlide, { transform: [`translateX(${flip * 100}%)`, "translateX(0)"] }, { ease: [0.77, 0, 0.175, 1], at: 0 }],
          [toSlide.firstElementChild, { transform: [`translateX(${flip * -30}%)`, "translateX(0)"] }, { ease: [0.77, 0, 0.175, 1], at: 0 }]
        ], {
          duration: window.innerWidth < 699 ? 0.8 : 1
        });
        break;
      case "distortion":
        flip = direction === "next" ? document.dir === "rtl" ? -1 : 1 : document.dir === "rtl" ? 1 : -1;
        if (direction === "next" || direction.dir === "rtl" && direction === "prev") {
          fromSlide.firstElementChild.style.transformOrigin = "100% 50%";
          toSlide.firstElementChild.style.transformOrigin = "0% 50%";
        } else {
          fromSlide.firstElementChild.style.transformOrigin = "0% 50%";
          toSlide.firstElementChild.style.transformOrigin = "100% 50%";
        }
        animationControls = animateSequence13([
          [fromSlide, { transform: ["translateX(0)", `translateX(${flip * -100}%)`] }, { ease: [0.645, 0.045, 0.355, 1], at: 0 }],
          [fromSlide.firstElementChild, { transform: ["scaleX(1)", "scaleX(2.5)"] }, { ease: [0.645, 0.045, 0.355, 1], at: 0 }],
          [toSlide, { transform: [`translateX(${flip * 100}%)`, "translateX(0)"] }, { ease: [0.645, 0.045, 0.355, 1], at: 0 }],
          [toSlide.firstElementChild, { transform: [`translateX(${flip * -100}%) scaleX(2.5)`, "translateX(0) scaleX(1)"] }, { ease: [0.645, 0.045, 0.355, 1], at: 0 }]
        ], {
          duration: window.innerWidth < 699 ? 0.6 : 0.8
        });
        break;
      case "perspective":
        animationControls = animateSequence13([
          [fromSlide, { transform: ["scale(1)", "scale(0.9)"], opacity: [1, 0.3] }, { duration: 0.15, ease: [0.445, 0.05, 0.55, 0.95] }],
          { name: "middle", at: "+0.1" },
          [fromSlide, { transform: ["translateY(0) scale(0.9)", `translateY(-20%) scale(0.9)`], opacity: [0.3, 0] }, { at: "middle", ease: [0.77, 0, 0.175, 1] }],
          [toSlide, { transform: [`translateY(100%) scale(1)`, `translateY(0) scale(1)`], opacity: [1, 1] }, { at: "middle", ease: [0.77, 0, 0.175, 1] }],
          [toSlide.firstElementChild, { transform: [`translateY(-50%)`, "translateY(0)"] }, { at: "middle", ease: [0.77, 0, 0.175, 1] }]
        ], {
          duration: 0.7
        });
        break;
      case "scale_down":
        flip = direction === "next" ? document.dir === "rtl" ? -1 : 1 : document.dir === "rtl" ? 1 : -1;
        animationControls = animateSequence13([
          [fromSlide, { opacity: [1, 0], transform: ["scale(1)", `scale(0.7)`] }, { duration: 0.5, ease: [0.645, 0.045, 0.355, 1], at: 0 }],
          [toSlide, { opacity: [1, 1], transform: [`translateY(${flip * 100}%)`, "translateY(0)"] }, { duration: 0.7, ease: [0.645, 0.045, 0.355, 1], at: "<" }]
        ]);
        break;
    }
    return animationControls;
  }
  onSlideChange(event) {
    this.querySelectorAll("video-media").forEach((video) => {
      customElements.upgrade(video);
      video.pause();
    });
    if (event.detail.cell.getAttribute("data-slide-type") === "video") {
      event.detail.cell.querySelectorAll("video-media").forEach((video) => video.play({ restart: true }));
    }
  }
  onPlayerStart(event) {
    this.#navigationButtonAnimationControls?.complete();
    requestAnimationFrame(() => {
      this.querySelectorAll('.scroll-marker[aria-current="false"] .scroll-marker-group__line-progress').forEach((button) => button.style.transform = null);
    });
    this.#navigationButtonAnimationControls = animate22(
      this.querySelectorAll('.scroll-marker[aria-current="true"] .scroll-marker-group__line-progress'),
      {
        transform: ["scaleX(0)", "scaleX(1)"]
      },
      {
        duration: event.detail.duration,
        ease: "linear"
      }
    );
  }
  onPlayerStop() {
    this.#navigationButtonAnimationControls?.complete();
  }
  onPlayerPause() {
    this.#navigationButtonAnimationControls?.pause();
    this.selectedCell.querySelectorAll("video-media").forEach((video) => video.pause());
  }
  onPlayerResume() {
    this.#navigationButtonAnimationControls?.play();
    this.selectedCell.querySelectorAll("video-media").forEach((video) => video.play());
  }
};
if (!window.customElements.get("slideshow-carousel")) {
  window.customElements.define("slideshow-carousel", Slideshow);
}

// js/sections/image-annotations.js
var ImageAnnotationsCarousel = class extends EffectCarousel {
  #modalAbortController;
  connectedCallback() {
    super.connectedCallback();
    this.#modalAbortController = new AbortController();
    this.addEventListener("carousel:change", () => {
      this.querySelectorAll("x-modal[open]").forEach((modal) => modal.hide());
    }, { signal: this.#modalAbortController.signal });
  }
  disconnectedCallback() {
    this.#modalAbortController?.abort();
  }
};
if (!window.customElements.get("image-annotations-carousel")) {
  window.customElements.define("image-annotations-carousel", ImageAnnotationsCarousel);
}

// js/sections/newsletter-popup.js
var NewsletterPopup = class extends Modal {
  constructor() {
    super();
    this.addEventListener("dialog:force-close", () => {
      this._shouldPersistDismissal = true;
    }, { capture: true });
  }
  connectedCallback() {
    super.connectedCallback();
    this.getShadowPartByName("overlay")?.addEventListener("click", () => {
      this._shouldPersistDismissal = true;
    }, { capture: true, signal: this.abortController.signal });
    if (Shopify.designMode || this.open || !this.shouldAppearAutomatically) {
      return;
    }
    setTimeout(() => this.show(), this.apparitionDelay);
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppendToBody() {
    return false;
  }
  get dismissDurationDays() {
    const days = parseInt(this.getAttribute("dismiss-duration-days") || "0", 10);
    if (Number.isNaN(days) || days < 1) {
      return 0;
    }
    return days;
  }
  get dismissStorageKey() {
    return "theme:popup-dismissed";
  }
  _hasActiveDismissal() {
    if (this.hasAttribute("only-once") || this.dismissDurationDays === 0) {
      return false;
    }
    try {
      const stored = localStorage.getItem(this.dismissStorageKey);
      if (!stored) {
        return false;
      }
      const dismissedAt = parseInt(stored, 10);
      if (Number.isNaN(dismissedAt)) {
        localStorage.removeItem(this.dismissStorageKey);
        return false;
      }
      const durationMs = this.dismissDurationDays * 24 * 60 * 60 * 1e3;
      if (Date.now() - dismissedAt < durationMs) {
        return true;
      }
      localStorage.removeItem(this.dismissStorageKey);
      return false;
    } catch {
      return false;
    }
  }
  _markDismissed() {
    try {
      localStorage.setItem(this.dismissStorageKey, String(Date.now()));
    } catch {
    }
  }
  get shouldAppearAutomatically() {
    if (localStorage.getItem("theme:popup-filled") === "true") {
      return false;
    }
    if (this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true") {
      return false;
    }
    if (this._hasActiveDismissal()) {
      return false;
    }
    return true;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      if (this.hasAttribute("only-once")) {
        localStorage.setItem("theme:popup-appeared", "true");
      }
      this._shouldPersistDismissal = false;
      return;
    }
    if (name === "open" && oldValue !== null && newValue === null) {
      if (!Shopify.designMode && this._shouldPersistDismissal && !this.hasAttribute("only-once") && this.dismissDurationDays > 0) {
        this._markDismissed();
      }
      this._shouldPersistDismissal = false;
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}

// js/common/klaviyo-subscription.js
var KlaviyoSubscription = class extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector(".klaviyo-subscription__form");
    this.inputWrapper = this.querySelector(".klaviyo-subscription__input");
    this.emailInput = this.querySelector('input[type="email"]');
    this.resultsElement = this.querySelector(".klaviyo-subscription__results");
    this.hideOnSuccessElements = this.querySelectorAll(".klaviyo-subscription__hide-on-success");
    this.submitButton = this.querySelector('button[type="submit"]');
    this.apiKey = this.getAttribute("api-key");
    this.listId = this.getAttribute("list-id");
    this.source = this.getAttribute("source") || "Newsletter";
    this.successText = this.getAttribute("success-text");
    this.errorText = this.getAttribute("error-text");
    this.form?.addEventListener("submit", this._onFormSubmitted.bind(this));
  }

  async _onFormSubmitted(event) {
    event.preventDefault();
    this.#clearResults();

    const email = this.emailInput?.value?.trim();
    if (!email) {
      this.#showError(this.errorText || "Please enter a valid email address.");
      return;
    }

    if (!this.apiKey || !this.listId) {
      this.#showError("Missing Klaviyo configuration.");
      return;
    }

    this.submitButton?.setAttribute("aria-busy", "true");

    try {
      const newsletterUrl = "https://a.klaviyo.com/client/subscriptions/?company_id=" + this.apiKey;
      const payload = {
        data: {
          type: "subscription",
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email
                }
              }
            }
          },
          relationships: { list: { data: { type: "list", id: `${this.listId}` } } }
        }
      };

      const response = await fetch(newsletterUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "revision": "2024-06-15"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const filledStorageKey = this.getAttribute("filled-storage-key");
        if (filledStorageKey) {
          localStorage.setItem(filledStorageKey, "true");
        }
        this.#showSuccess(this.successText || "Thanks for subscribing.");
        this.form?.reset();
      } else {
        let errorDetail = "";
        try {
          const errors = await response.json();
          errorDetail = errors?.errors?.[0]?.detail || "";
        } catch {
        }
        this.#showError(errorDetail || this.errorText || "Something went wrong. Please try again.");
      }
    } catch {
      this.#showError(this.errorText || "Something went wrong. Please try again.");
    } finally {
      this.submitButton?.setAttribute("aria-busy", "false");
    }
  }

  #clearResults() {
    if (!this.resultsElement) return;
    this.resultsElement.hidden = true;
    this.resultsElement.innerHTML = "";
    this.hideOnSuccessElements?.forEach((element) => {
      element.hidden = false;
    });
  }

  #showSuccess(message) {
    if (!this.resultsElement) return;
    this.resultsElement.innerHTML = `<div class="banner banner--success text-center"><div class="banner__content">${message}</div></div>`;
    this.resultsElement.hidden = false;
    if (this.inputWrapper) this.inputWrapper.hidden = true;
    this.hideOnSuccessElements?.forEach((element) => {
      element.hidden = true;
    });
  }

  #showError(message) {
    if (!this.resultsElement) return;
    this.resultsElement.innerHTML = `<div class="banner banner--error text-center"><div class="banner__content">${message}</div></div>`;
    this.resultsElement.hidden = false;
  }
};

if (!window.customElements.get("klaviyo-subscription")) {
  window.customElements.define("klaviyo-subscription", KlaviyoSubscription);
}


// js/theme.js
import { Delegate as Delegate11 } from "vendor";
(() => {
  const delegateDocument = new Delegate11(document.documentElement);
  delegateDocument.on("click", 'a[href*="#"]', (event, target) => {
    if (event.defaultPrevented || target.matches("[allow-hash-change]") || target.pathname !== window.location.pathname || target.search !== window.location.search) {
      return;
    }
    const url = new URL(target.href);
    if (url.hash === "") {
      return;
    }
    const anchorElement = document.querySelector(url.hash);
    if (anchorElement) {
      event.preventDefault();
      anchorElement.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto" });
      document.documentElement.dispatchEvent(new CustomEvent("hashchange:simulate", { bubbles: true, detail: { hash: url.hash } }));
    }
  });
  if (navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) {
    document.head.querySelector('meta[name="viewport"]').content = "width=device-width, initial-scale=1.0, height=device-height, minimum-scale=1.0, maximum-scale=1.0";
  }
  Array.from(document.querySelectorAll(".prose table")).forEach((table) => {
    table.outerHTML = '<div class="table-scroller">' + table.outerHTML + "</div>";
  });
})();
export {
  AccordionDisclosure,
  AnnouncementBar,
  BlogPostList,
  ButtonContent,
  BuyButtons,
  CarouselNavigation,
  CarouselNextButton,
  CarouselPageIndicator,
  CarouselPrevButton,
  CartCount,
  CartDiscountBanner,
  CartDiscountField,
  CartDiscountRemoveButton,
  CartDrawer,
  CartNote,
  CollectionFacetsFloatingButton,
  CopyButton,
  CountrySelector,
  CustomCursor,
  DialogCloseButton,
  DialogElement,
  Drawer,
  DrawerPopover,
  EffectCarousel,
  FacetLink,
  FacetsDrawer,
  FacetsForm,
  FeaturedCollectionsTabs,
  FreeShippingBar,
  GestureArea,
  GiftCardRecipient,
  Header,
  HeaderDropdownMenu,
  HeaderMegaMenu,
  HeaderMenuDrawer,
  HeaderMenuDrawerPanel,
  HeightObserver,
  IngredientGlossary,
  KlaviyoSubscription,
  LayoutSelectorButton,
  LineItem,
  LoadingBar,
  MarqueeText,
  MediaPlayButton,
  Modal,
  ModelMedia,
  OpenLightBoxButton,
  Player,
  Popover,
  PredictiveSearch,
  PriceRange,
  ProductCard,
  ProductForm,
  ProductGallery,
  ProductList,
  ProductLoader,
  ProductRecommendations,
  ProductRerender,
  ProductStickyAddToCart,
  ProgressBar,
  QrCode,
  QuantityInput,
  QuantitySelector,
  QuickBuyModal,
  RecentlyViewedProducts,
  SafeSticky,
  SandwichVisibility,
  ScrollCarousel,
  ScrollShadow,
  SearchResultPanel,
  ShareButton,
  ShippingEstimator,
  Slideshow,
  SortByPopover,
  Tabs,
  Toast,
  VariantPicker,
  VideoMedia,
  announceError,
  announceStatus,
  cachedFetch,
  createMediaImg,
  debounce,
  deepQuerySelector,
  extractSectionId,
  fetchCart,
  generateSrcset,
  getSiblings,
  getUntransformedBoundingClientReact,
  imageLoaded,
  matchesMediaQuery,
  mediaQueryListener,
  throttle,
  videoLoaded,
  waitForEvent
};